/**
 * The App Shell: the layout that holds the grid together with the widgets around it
 * (Requirements 1.4–1.12).
 *
 * Top to bottom: a toolbar, the name box and formula bar, the canvas grid, the status
 * bar, and the sheet tabs. The shell owns the only reference to the
 * {@link KernelClient} and hands each widget the narrow capability it needs — the grid
 * gets the three kernel channels it reads and writes through, the status bar gets
 * numbers, the tabs get callbacks — so no widget can reach the Workbook directly.
 *
 * Three responsibilities beyond layout:
 *
 * **It routes `changedRegions`.** Every mutation broadcast invalidates the
 * intersecting tiles, re-reads the sheet geometry, and refreshes the formula bar and
 * status bar when the active selection was touched. That is the one subscription the
 * whole UI hangs off, which keeps the grid, the bar, and the aggregate from drifting
 * out of step.
 *
 * **It reports notices.** Command notices, a rejected command's envelope (the
 * 10-sheet ceiling of Requirement 1.11 arrives this way, carrying the limit and the
 * requested count per Requirement 16.1), and a worker recovery all land in the status
 * bar's live region rather than in a `console.log`.
 *
 * **It keeps the test handle alive.** `window.__spreadsheet` predates the shell and the
 * Playwright suite drives Requirement 14.1 through it, so the shell installs the same
 * handle, now backed by real UI state.
 */

import { addrToA1, rangeToA1 } from '../core/a1.js';
import type {
  Command,
  CommandResult,
  HistoryCommandResult,
  PasteVariant,
} from '../core/command/index.js';
import type { Addr, CellValue, Direction, Range, SheetId } from '../core/types.js';
import {
  createKernelAggregator,
  type AggResult,
  type SelectionAggregator,
} from '../grid/aggregate.js';
import type { SelectionState } from '../grid/interaction.js';
import type { PaintStats } from '../grid/painter.js';
import type { SheetView } from '../grid/viewTile.js';
import { KernelUnavailableError, type KernelClient } from '../kernel/client.js';
import { tileCell, type TileTransportMode } from '../kernel/tile.js';
import { handleKey, type ShortcutAction } from '../grid/shortcuts.js';
import { createCheatSheet, type CheatSheet } from './cheatSheet.js';
import {
  createClipboardBridge,
  fromClipboardEvent,
  type ClipboardBridge,
  type ClipboardRead,
} from './clipboard.js';
import { downloadExport, exportSheet, type ExportFormat } from './exportService.js';
import { streamImport } from './csvStreamImport.js';
import { uploadColumnarFile } from '../core/apiClient.js';
import { createRemoteDuckDbSource } from './remoteDuckDbSource.js';
import { pythonEngine } from '../python/pythonEngine.js';
import { el, installStyles } from './dom.js';
import { createFindBar } from './findBar.js';
import { createFormulaBar, type FormulaBar } from './formulaBar.js';
import { createGridView, type GridView } from './gridView.js';
import type { ChartPanel } from './chartPanel.js';
import type { PivotPanel } from './pivotPanel.js';
import { createSheetTabs, type SheetTabs } from './sheetTabs.js';
import { createCommandPalette, type CommandPaletteItem } from './commandPalette.js';
import { createFormatDrawer } from './formatDrawer.js';
import { createAddDataModal } from './addDataModal.js';
import { createProductModePicker, type ProductMode } from './productMode.js';
import { createEditorModeWorkspace } from './editorModes.js';
import { cloudSync } from '../io/cloudSync.js';
import { createStatusBar, type ProgressState, type StatusBar } from './statusBar.js';
import { streamZipImport } from './zipStreamImport.js';
import { exportWorkbookZip, downloadWorkbookZip } from './zipExportService.js';
import { SHELL_CSS, SHELL_STYLE_ID } from './styles.js';
import { numberFormat, type NumberFormat } from '../core/format/numberFormat.js';
import type { CellStylePatch } from '../core/format/style.js';

/** Sheet id `createWorkbook` gives a fresh workbook's first sheet. */
export const DEFAULT_SHEET_ID = 'sheet-1';

/** A tile flattened into values, for callers outside the page's memory. */
export interface TileReadout {
  rows: number;
  cols: number;
  values: CellValue[];
}

/**
 * The handle the Playwright suite drives (`window.__spreadsheet`).
 *
 * Every method is a pass-through to the same client call a keystroke makes, plus the
 * view accessors an end-to-end test needs. Typed arrays cannot cross out of the page,
 * so `tile` flattens a tile into plain values.
 */
export interface SpreadsheetHandle {
  dispatch(command: Command): Promise<CommandResult>;
  tile(range: Range): Promise<TileReadout>;
  undo(): Promise<HistoryCommandResult>;
  redo(): Promise<HistoryCommandResult>;
  diagnostics(): Promise<string>;
  transport(): TileTransportMode;
  restarts(): number;
  /** The selected range and active cell (Requirement 1.9). */
  selection(): { active: Addr; range: Range };
  select(range: Range): void;
  /** The status-bar aggregate for the current selection (Requirement 1.10). */
  aggregate(): Promise<AggResult>;
  /** The name box text, as displayed (Requirement 1.9). */
  nameBox(): string;
  /** The formula bar text, as displayed (Requirement 1.9). */
  formulaBar(): string;
  /** The status bar's text, as displayed (Requirement 1.10). */
  statusText(): string;
  /** Sheet tabs and the active one (Requirements 1.3, 1.11, 1.12). */
  sheets(): { id: SheetId; name: string; active: boolean }[];
  activateSheet(sheet: SheetId): Promise<void>;
  addSheet(): Promise<CommandResult>;
  /** Statistics of the last painted frame (Requirement 1.5). */
  lastFrame(): PaintStats | null;
  /** The most recent notice shown in the status bar. */
  notice(): string;
  /** True while the in-cell editor is open (Requirements 2.1–2.4). */
  editing(): boolean;
  /** The in-cell editor's current text, or `null` when closed. */
  editorText(): string | null;
  /** The searchable shortcut list, filtered by `query` (Requirement 3.7). */
  shortcuts(query?: string): string[];
  /**
   * Runs a find from the current selection and moves the selection to the match
   * (Requirement 7.9). Resolves with the match, or `null` when the sheet holds none.
   */
  find(term: string): Promise<Addr | null>;
  /** Replaces every occurrence in the search scope (Requirement 7.10). */
  replaceAll(term: string, replacement: string): Promise<CommandResult>;
  /** Row runs the active filters hide (Requirements 7.4, 7.8). */
  hiddenRows(): [number, number][];
  /** Progress of the operation currently running, or `null` (Requirement 14.7). */
  progress(): ProgressState | null;
  /** Copies the selection to the system clipboard (Requirement 12.6). */
  copy(): Promise<void>;
  /** Cuts the selection; the paste clears the source (Requirement 12.6). */
  cut(): Promise<void>;
  /**
   * Pastes the clipboard into the selection (Requirements 12.6, 12.7, 12.8, 12.10).
   *
   * Resolves with the command's result, or `null` when the clipboard held nothing.
   */
  paste(variant?: PasteVariant): Promise<CommandResult | null>;
  /**
   * Loads the lazily-chunked pivot feature without creating a pivot
   * (Requirements 8, 14.3).
   *
   * Lets the local-only test separate "fetch the feature's code" from "run the
   * operation", so its zero-request assertion is about the computation.
   */
  warmPivot(): Promise<void>;
}

export interface AppShellOptions {
  client: KernelClient;
  host: HTMLElement;
  /** Sheet to open first. Defaults to {@link DEFAULT_SHEET_ID}. */
  sheet?: SheetId;
}

export interface AppShell {
  /** Resolves once the first sheet has been loaded and painted. */
  ready: Promise<void>;
  handle: SpreadsheetHandle;
  grid: GridView;
  /** The searchable shortcut panel (Requirement 3.7). */
  cheatSheet: CheatSheet;
  /**
   * Loads and opens the pivot builder (Requirements 8.1, 8.3, 14.3).
   *
   * The panel and the Pivot_Engine are lazy chunks, so the first call pays for one
   * dynamic import; later calls resolve immediately.
   */
  openPivotPanel(): Promise<PivotPanel>;

  /**
   * Opens the chart panel, loading its chunk on the first call (Requirements 9.1,
   * 14.3).
   */
  openChartPanel(): Promise<ChartPanel>;
  /** The active sheet's geometry, as last read from the kernel. */
  sheetView(): SheetView | null;
  /** Sets the diagnostics line the integration suite reads (`kernel` testid). */
  announce(text: string): void;
  /** Marks the app interactive. The integration suite waits on this line. */
  markReady(text?: string): void;
  /** Shows a notice in the status bar. */
  setNotice(text: string): void;
  dispose(): void;
}

function sameRange(a: Range, b: Range): boolean {
  return a.sheet === b.sheet && a.r0 === b.r0 && a.c0 === b.c0 && a.r1 === b.r1 && a.c1 === b.c1;
}

/** The next unused `SheetN` name, so the add button never trips a name collision. */
function nextSheetName(tabs: readonly { name: string }[]): string {
  const taken = new Set(tabs.map((t) => t.name.toLowerCase()));
  for (let n = tabs.length + 1; ; n++) {
    const candidate = `Sheet${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

export function createAppShell(options: AppShellOptions): AppShell {
  const { client, host } = options;
  installStyles(SHELL_CSS, SHELL_STYLE_ID);

  const firstSheet = options.sheet ?? DEFAULT_SHEET_ID;
  const diagnosticsLine = el('span', { className: 'ws-diag', testid: 'kernel' });
  const readyLine = el('span', { className: 'ws-diag', testid: 'ready' });

  let noticeText = '';
  let presenceTimer: ReturnType<typeof setInterval> | null = null;
  let lastPresenceSentAt = 0;
  let lastFrame: PaintStats | null = null;
  /** Progress of the operation currently running, or `null` (Requirement 14.7). */
  let lastProgress: ProgressState | null = null;
  /** The selection an in-flight aggregate belongs to, so a late reply can be dropped. */
  let aggregateFor: Range | null = null;

  const statusBar: StatusBar = createStatusBar();

  let noticeTimer: ReturnType<typeof setTimeout> | null = null;
  function setNotice(text: string): void {
    noticeText = text;
    statusBar.setMessage(text);
    if (noticeTimer !== null) {
      clearTimeout(noticeTimer);
      noticeTimer = null;
    }
    if (text.length > 0) {
      noticeTimer = setTimeout(() => {
        noticeText = '';
        statusBar.setMessage('');
        noticeTimer = null;
      }, 3000);
    }
  }

  let activeProductMode: ProductMode = (() => { try { const saved = localStorage.getItem('numix-product-mode'); return saved === 'document' || saved === 'slides' ? saved : 'spreadsheet'; } catch { return 'spreadsheet'; } })();
  const modePicker = createProductModePicker(activeProductMode);
  const editorModeWorkspace = createEditorModeWorkspace();

  /** Sends a command and surfaces whatever came back (Requirements 1.11, 16.1). */
  async function dispatch(command: Command): Promise<CommandResult> {
    try {
      const result = await client.dispatch(command);
      if (!result.ok) setNotice(result.error.message);
      else setNotice(result.notices.length > 0 ? result.notices.join(' · ') : '');
      return result;
    } catch (e: unknown) {
      const message =
        e instanceof KernelUnavailableError
          ? e.envelope.message
          : e instanceof Error
            ? e.message
            : String(e);
      setNotice(message);
      throw e;
    }
  }

  // The aggregate is computed in the worker (Requirement 1.10): the shell asks, it does
  // not scan.
  const aggregator: SelectionAggregator = createKernelAggregator(client);

  const formulaBar: FormulaBar = createFormulaBar({
    onNavigate: (range: Range) => {
      grid.selectRange(range);
      grid.focus();
    },
    onNavigateError: (text: string) => {
      setNotice(`${text.length === 0 ? 'that' : text} is not a cell reference`);
    },
    onCommit: (text: string) => {
      const at = grid.selection().active;
      void dispatch({ k: 'setCell', addr: at, input: text }).then(() => grid.focus());
    },
  });

  const cheatSheet: CheatSheet = createCheatSheet({
    onClose: () => grid.focus(),
  });

  /**
   * The scope a find or a replace covers: the sheet's data region.
   *
   * Not the whole 5,000,000 × 16,384 address space — outside the used range every cell is
   * blank and no non-empty term can match one — and not the selection, because
   * Requirement 7.9's "next Cell" is about walking the sheet rather than a rectangle the
   * user happens to have highlighted.
   */
  function searchScope(): Range {
    const used = grid.sheetView()?.usedRange;
    return used ?? { sheet: grid.sheet(), r0: 0, c0: 0, r1: 0, c1: 0 };
  }

  /** Finds the next match and moves the selection onto it (Requirement 7.9). */
  async function findNext(term: string): Promise<Addr | null> {
    if (term.length === 0) {
      findBar.setResult('Type something to find.');
      return null;
    }
    const at = await client.find(searchScope(), term, grid.selection().active);
    if (at === null) {
      findBar.setResult(`No cell contains "${term}".`);
      return null;
    }
    grid.selectRange({ sheet: at.sheet, r0: at.row, c0: at.col, r1: at.row, c1: at.col });
    findBar.setResult(`Found in ${addrToA1(at)}.`);
    return at;
  }

  /** Replaces every occurrence in the search scope (Requirement 7.10). */
  async function replaceAll(term: string, replacement: string): Promise<CommandResult> {
    const result = await dispatch({
      k: 'replaceAll',
      scope: searchScope(),
      term,
      replacement,
    });
    // The count comes back in the command's notices, so the bar reports exactly what the
    // Query_Engine did rather than counting again.
    findBar.setResult(result.ok ? result.notices.join(' · ') : result.error.message);
    return result;
  }

  const findBar = createFindBar({
    onFindNext: (term: string) => void findNext(term),
    onReplaceAll: (term: string, replacement: string) => void replaceAll(term, replacement),
    onClose: () => grid.focus(),
    // The bar has focus while it is open, so the binding table is consulted here too:
    // otherwise `Ctrl+S` would reach the browser's save dialog and `Ctrl+H` its history,
    // both of which Requirement 3.6 says the app suppresses. Only modified combinations
    // are forwarded — plain keys and `Tab` belong to the text field.
    onKey: (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const binding = handleKey(event, 'grid');
      if (binding === null) return;
      grid.runAction(binding.command);
    },
  });

  const onDocFallbackKeyDown = (event: KeyboardEvent): void => {
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName?.toLowerCase() ?? '';
    const isInput =
      tagName === 'input' || tagName === 'textarea' || target?.isContentEditable === true;
    if (!isInput) {
      const mode = grid.isEditing() ? 'edit' : 'grid';
      const binding = handleKey(event, mode);
      if (binding !== null) {
        const isSurface =
          target?.classList.contains('grid-surface') ||
          target?.getAttribute('data-testid') === 'grid';
        if (!isSurface) {
          grid.runAction(binding.command);
          grid.focus();
        }
      }
    }
  };
  document.addEventListener('keydown', onDocFallbackKeyDown, true);

  /* ------------------------------------------------------------- clipboard */

  /**
   * The Clipboard_Manager (Requirements 12.6–12.10).
   *
   * The shell owns it, not the grid, for the same reason it owns the find bar: the
   * system clipboard is a page-level resource and the notices belong in the status
   * bar. The payload itself is assembled in the worker (`client.clip`), because
   * formula sources and cell styles live there.
   *
   * **Why the paste reads the clipboard rather than listening for a paste event.**
   * Requirement 3.6 has the Shortcut_Handler suppress the browser default for every
   * mapped combination, and `Ctrl+V`'s default *is* the native paste event. Having
   * suppressed it, the app has to ask for the clipboard itself. A `paste` listener is
   * still installed for the gestures that bypass the keyboard — the context menu, and
   * middle-click on X11 — so both routes end in the same command.
   */
  const clipboard: ClipboardBridge = createClipboardBridge();

  async function copySelection(cut: boolean): Promise<void> {
    const range = grid.selection().range;
    try {
      const payload = await client.clip(range);
      const written = await clipboard.write(cut ? { ...payload, cut: true } : payload);
      const what = `${cut ? 'Cut' : 'Copied'} ${rangeToA1(range)}`;
      setNotice(
        written
          ? `${what}.`
          : `${what} inside this tab; the browser refused access to the system clipboard.`,
      );
    } catch (e: unknown) {
      setNotice(e instanceof Error ? e.message : String(e));
    }
  }

  /** Pastes whatever the clipboard holds (Requirements 12.6, 12.7, 12.8, 12.10). */
  async function pasteInto(
    variant: PasteVariant = 'all',
    read?: ClipboardRead,
  ): Promise<CommandResult | null> {
    const held = read ?? (await clipboard.read());
    if (held.payload === null) {
      setNotice('The clipboard is empty.');
      return null;
    }
    const selection = grid.selection().range;
    const result = await dispatch({
      k: 'paste',
      at: { sheet: selection.sheet, row: selection.r0, col: selection.c0 },
      variant,
      payload: held.payload,
      selection,
    });
    if (result.ok && held.source === 'external') {
      // Where the text came from is worth saying, and it is *additional* to whatever
      // the command reported: a paste from another application into a single cell both
      // expanded its destination (Requirement 12.10) and arrived as delimited text
      // (Requirement 12.8), and the user should hear both.
      const rows = held.payload.rows;
      setNotice(
        [...result.notices, `Pasted ${rows} row${rows === 1 ? '' : 's'} of external text.`].join(
          ' · ',
        ),
      );
    }
    return result;
  }

  /**
   * Shortcuts the grid does not own (Requirements 3.1, 3.7).
   *
   * Sheet activation, the clipboard, and the cheat sheet live here because the shell
   * owns the tab strip, the system clipboard, and the panels. Everything whose
   * subsystem lands later — save (task 23), the format dialog — falls through, and
   * the grid reports it as unavailable. The browser default was already suppressed by
   * the dispatcher, so `Ctrl+F` still does not open the browser's find bar
   * (Requirement 3.6).
   */
  function getColName(index: number): string {
    let name = '';
    let i = index;
    while (i >= 0) {
      name = String.fromCharCode(65 + (i % 26)) + name;
      i = Math.floor(i / 26) - 1;
    }
    return name;
  }

  function onAction(action: ShortcutAction): boolean {
    switch (action) {
      case 'showShortcuts':
        cheatSheet.open();
        return true;
      case 'copy':
        void copySelection(false);
        return true;
      case 'cut':
        void copySelection(true);
        return true;
      case 'paste':
        void pasteInto('all');
        return true;
      case 'find':
        findBar.open('find');
        return true;
      case 'replace':
        findBar.open('replace');
        return true;
      case 'nextSheet':
        void stepSheet(1);
        return true;
      case 'prevSheet':
        void stepSheet(-1);
        return true;
      case 'toggleFilters':
        toggleFilterMode();
        return true;
      case 'formatDialog':
        openFormatCellsDialog();
        return true;
      default:
        return false;
    }
  }

  /** Ctrl+1 — Format Cells dialog (number / alignment / font / border tabs). */
  function openFormatCellsDialog(): void {
    const body = el('div');
    const tabs = el('div', { attrs: { style: 'display:flex;gap:4px;border-bottom:1px solid #e2e8f0;margin-bottom:12px;' } });
    const panels = el('div');
    const fmtState: { bold: boolean; italic: boolean; underline: boolean; decimals: number; category: string; pattern: string } = {
      bold: false, italic: false, underline: false, decimals: 2, category: 'general', pattern: '',
    };

    const tab = (label: string, content: HTMLElement): HTMLElement => {
      const btn = el('button', {
        text: label,
        attrs: { style: 'padding:6px 12px;border:none;background:transparent;cursor:pointer;font:inherit;border-bottom:2px solid transparent;' },
      });
      btn.addEventListener('click', () => {
        panels.replaceChildren(content);
        tabs.querySelectorAll('button').forEach((b) => (b.style.borderBottomColor = 'transparent'));
        btn.style.borderBottomColor = '#0284c7';
      });
      tabs.appendChild(btn);
      return btn;
    };

    // Number tab
    const numberPanel = el('div');
    const catSelect = el('select', { attrs: { style: 'padding:4px 8px;border:1px solid #cbd5e1;border-radius:4px;font:inherit;' } }) as HTMLSelectElement;
    ['general', 'number', 'currency', 'percentage', 'date', 'time', 'text'].forEach((c) => {
      const opt = el('option', { text: c.charAt(0).toUpperCase() + c.slice(1), attrs: { value: c } });
      catSelect.appendChild(opt);
    });
    catSelect.value = fmtState.category;
    catSelect.addEventListener('change', () => { fmtState.category = catSelect.value; });
    const decimals = el('input', {
      attrs: { type: 'number', min: '0', max: '10', value: '2', style: 'width:60px;padding:4px 8px;border:1px solid #cbd5e1;border-radius:4px;' },
    }) as HTMLInputElement;
    decimals.addEventListener('change', () => { fmtState.decimals = parseInt(decimals.value, 10) || 0; });
    numberPanel.append(el('label', { text: 'Category: ', attrs: { style: 'display:block;margin-bottom:8px;' } }), catSelect);
    numberPanel.append(el('label', { text: 'Decimals: ', attrs: { style: 'display:block;margin-top:8px;' } }), decimals);

    // Alignment tab
    const alignPanel = el('div');
    alignPanel.appendChild(el('p', { text: 'Use the toolbar buttons for alignment, or click an option below:', attrs: { style: 'margin:0 0 8px 0;color:#64748b;' } }));
    ['left', 'center', 'right'].forEach((a) => {
      const btn = el('button', {
        text: `Align ${a}`,
        attrs: { style: 'margin-right:6px;padding:4px 10px;border:1px solid #cbd5e1;border-radius:4px;background:#ffffff;cursor:pointer;' },
      });
      btn.addEventListener('click', () => { void dispatch({ k: 'applyStyle', range: grid.selection().range, patch: { hAlign: a as 'left' | 'center' | 'right' } }); });
      alignPanel.appendChild(btn);
    });

    // Borders tab
    const borderPanel = el('div');
    borderPanel.appendChild(el('p', { text: 'Use the Borders button on the toolbar.', attrs: { style: 'margin:0;color:#64748b;' } }));

    tab('Number', numberPanel);
    tab('Alignment', alignPanel);
    tab('Borders', borderPanel);

    body.append(tabs, panels);
    panels.appendChild(numberPanel);
    tabs.firstElementChild!.dispatchEvent(new Event('click'));

    let closeFmt: () => void = () => {};
    closeFmt = openModal({
      title: 'Format Cells (Ctrl+1)',
      width: 460,
      body,
      actions: [
        { label: 'Cancel', onClick: () => closeFmt() },
        {
          label: 'Apply',
          primary: true,
          onClick: () => {
            void dispatch({ k: 'applyNumberFormat', range: grid.selection().range, fmt: numberFormat({ category: fmtState.category as 'general' | 'number' | 'currency' | 'percentage' | 'date' | 'time' | 'text', decimals: fmtState.decimals }) });
            const patch: CellStylePatch = {};
            if (fmtState.bold) (patch as { bold?: boolean }).bold = true;
            if (fmtState.italic) (patch as { italic?: boolean }).italic = true;
            if (fmtState.underline) (patch as { underline?: boolean }).underline = true;
            if (Object.keys(patch).length > 0) {
              void dispatch({ k: 'applyStyle', range: grid.selection().range, patch });
            }
            setNotice('Format applied.');
            closeFmt();
          },
        },
      ],
    });
  }

  const filterTbBtn: HTMLElement | null = null;

  function toggleFilterMode(): void {
    const isCurrentlyVisible = grid.isFilterIconsVisible();
    if (!isCurrentlyVisible) {
      grid.setFilterIconsVisible(true);
      if (filterTbBtn) filterTbBtn.classList.add('active');
      setNotice('Filters enabled on Row 1 (Ctrl+Shift+L to toggle off)');
      // Deliberately *not* opening a column dropdown here. The dropdown's search box
      // takes the keyboard, and it is built asynchronously from the kernel's distinct
      // values — so auto-opening it moved focus into a text field some milliseconds
      // after the keystroke, and every shortcut pressed in that window stopped being
      // suppressed (Requirement 3.6). Enabling the filter row is what this binding
      // promises; a dropdown opens on a click of the filter icon.
    } else {
      closeColFilterPopup();
      grid.setFilterIconsVisible(false);
      if (filterTbBtn) filterTbBtn.classList.remove('active');
      const currentSheet = grid.selection().range.sheet;
      void dispatch({ k: 'clearFilters', sheet: currentSheet });
      setNotice('Filters disabled & cleared');
    }
  }

  const localGridSource = {
      view: (range: Range) => client.view(range),
      sheetView: (sheet: SheetId) => client.sheetView(sheet),
      dispatch,
      dispatchBatch: (commands: readonly Command[], label?: string) => client.dispatchBatch(commands, label ?? 'Batch'),
      cellContent: (at: Addr) => client.cellContent(at),
      blockEdge: (at: Addr, dir: Direction) => client.blockEdge(at, dir),
      lastUsedCell: (sheet: SheetId) => client.lastUsedCell(sheet),
      undo: () => client.undo(),
      redo: () => client.redo(),
  };
  const remoteParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const remoteTable = remoteParams?.get('remoteTable');
  const remoteRows = Number(remoteParams?.get('remoteRows') ?? 0);
  const remoteCols = Number(remoteParams?.get('remoteCols') ?? 0);
  const source = remoteTable && remoteRows > 0 && remoteCols > 0
    ? createRemoteDuckDbSource({ table: remoteTable, rowCount: remoteRows, columnCount: remoteCols, fallback: localGridSource })
    : localGridSource;
  const grid: GridView = createGridView({
    source,
    sheet: firstSheet,
    onSelectionChange: (selection) => void refreshSelection(selection),
    onMessage: setNotice,
    onSheetView: (view) => tabs.render(view.sheets, view.sheet),
    onFrame: (stats) => {
      lastFrame = stats;
    },
    onAction,
    onColumnHeaderClick: (col, rect) => {
      // Filter popup should only open when filters have been explicitly enabled
      // by the user (Ctrl+Shift+L or the Filter toolbar button). Otherwise a
      // click on a column header is just a column-select, not a filter trigger.
      if (!grid.isFilterIconsVisible()) {
        setNotice('Filters are off. Press Ctrl+Shift+L or click Filter to enable.');
        return;
      }
      // Toggle: clicking the same column's header again closes the popup.
      if (activeColFilterPopup !== null && activeColFilterCol === col) {
        closeColFilterPopup();
        activeColFilterCol = null;
        return;
      }
      activeColFilterCol = col;
      void openInlineColumnFilter(col, rect);
    },
  });

  let activeColFilterPopup: HTMLElement | null = null;
  /** Column index the currently-open popup belongs to, for click-toggle close. */
  let activeColFilterCol: number | null = null;
  /**
   * Each open starts a generation id. If a second `openInlineColumnFilter`
   * starts before this one finishes, the generation guard at the bottom
   * discards our popup so the user never sees an orphan.
   */
  let colFilterBuildGen = 0;
  const closeColFilterPopup = (): void => {
    if (activeColFilterPopup !== null && activeColFilterPopup.parentNode) {
      activeColFilterPopup.parentNode.removeChild(activeColFilterPopup);
      activeColFilterPopup = null;
      grid.focus();
    }
    activeColFilterCol = null;
  };

  // Single global click handler for outside-click dismissal of the column
  // filter popup. Uses `mousedown` capture-phase + a one-shot suppression so
  // the very click that opens the popup does not immediately close it, and
  // any subsequent click outside the popup dismisses it once.
  let suppressColFilterOutsideClick = false;
  const onDocMouseDown = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (suppressColFilterOutsideClick) {
      suppressColFilterOutsideClick = false;
      return;
    }
    if (
      activeColFilterPopup !== null &&
      !activeColFilterPopup.contains(target)
    ) {
      closeColFilterPopup();
    }
  };
  document.addEventListener('mousedown', onDocMouseDown, true);

  async function openInlineColumnFilter(col: number, rect?: { x: number; y: number; w: number; h: number }): Promise<void> {
    closeColFilterPopup();
    // Each open starts a generation id. If a second `openInlineColumnFilter`
    // starts before this one finishes, the generation guard at the bottom
    // discards our popup so the user never sees an orphan.
    colFilterBuildGen += 1;
    const myGen = colFilterBuildGen;
    // Swallow the opening mousedown so the outside-click handler that runs in
    // capture phase does not immediately dismiss the popup we are about to
    // attach to the DOM. Without this flag the popup opens for a single frame
    // and is torn down before the next paint, which is the flicker users see.
    suppressColFilterOutsideClick = true;

    const currentSheet = grid.selection().range.sheet;
    const colNameStr = getColName(col);
    const canvasRect = grid.element.getBoundingClientRect();
    const leftPx = rect ? canvasRect.left + rect.x : canvasRect.left + 40;
    const topPx = rect ? canvasRect.top + rect.h + 2 : canvasRect.top + 30;

    const popup = el('div', {
      className: 'rz-filter-popup',
      attrs: { style: `left: ${Math.max(10, Math.min(leftPx, window.innerWidth - 290))}px; top: ${topPx}px;` },
    });

    // Section 1: Sort
    const sortTitle = el('div', { className: 'rz-filter-section-title', text: 'Sort' });
    const sortAsc = el('div', { className: 'rz-filter-item' }, [
      el('span', { text: '⇵' }),
      el('span', { text: 'Sort A-Z' }),
    ]);
    const sortDesc = el('div', { className: 'rz-filter-item' }, [
      el('span', { text: '⇵' }),
      el('span', { text: 'Sort Z-A' }),
    ]);
    const sortColor = el('div', { className: 'rz-filter-item' }, [
      el('span', { text: '🎨' }),
      el('span', { text: 'Sort by color' }),
    ]);

    sortAsc.addEventListener('click', () => {
      closeColFilterPopup();
      const used = grid.sheetView()?.usedRange;
      const r0 = used?.r0 ?? 0;
      const r1 = used?.r1 ?? 100;
      const c0 = used?.c0 ?? 0;
      const c1 = used?.c1 ?? 20;
      void dispatch({
        k: 'sort',
        range: { sheet: currentSheet, r0, c0, r1, c1 },
        keys: [{ col, direction: 'asc' }],
      }).then(() => setNotice(`Sorted Column ${colNameStr} A-Z`));
    });

    sortDesc.addEventListener('click', () => {
      closeColFilterPopup();
      const used = grid.sheetView()?.usedRange;
      const r0 = used?.r0 ?? 0;
      const r1 = used?.r1 ?? 100;
      const c0 = used?.c0 ?? 0;
      const c1 = used?.c1 ?? 20;
      void dispatch({
        k: 'sort',
        range: { sheet: currentSheet, r0, c0, r1, c1 },
        keys: [{ col, direction: 'desc' }],
      }).then(() => setNotice(`Sorted Column ${colNameStr} Z-A`));
    });

    const divider1 = el('div', { className: 'rz-filter-divider' });

    // Section 2: Filter
    const filterTitleLeft = el('span', { text: 'Filter' });
    const addCondLink = el('span', { className: 'rz-filter-add-cond', text: '+ Add condition' });
    const filterTitleRow = el('div', { className: 'rz-filter-section-title' }, [filterTitleLeft, addCondLink]);

    addCondLink.addEventListener('click', () => {
      setNotice('Quick condition: Filter input ready in search box');
      searchInput.focus();
    });

    // Scan values in column
    const used = grid.sheetView()?.usedRange;
    const maxR = Math.min(used?.r1 ?? 100, 300);
    const valueSet = new Set<string>();
    let hasBlanks = false;

    for (let r = 0; r <= maxR; r++) {
      const cell = await client.cellContent({ sheet: currentSheet, row: r, col });
      const displayVal = cell.display.trim();
      if (displayVal.length > 0) {
        valueSet.add(displayVal);
      } else {
        hasBlanks = true;
      }
    }

    const sortedValues = Array.from(valueSet).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    // Search Box
    const searchClearIcon = el('span', { className: 'rz-filter-search-clear', text: '✕' });
    const searchInput = el('input', {
      className: 'rz-filter-search-input',
      attrs: { placeholder: 'Search...' },
    }) as HTMLInputElement;

    searchClearIcon.addEventListener('click', () => {
      searchInput.value = '';
      searchInput.dispatchEvent(new Event('input'));
    });

    const searchBox = el('div', { className: 'rz-filter-search-box' }, [searchInput, searchClearIcon]);

    // Checklist Container
    const checklist = el('div', { className: 'rz-filter-checklist' });

    const selectAllCb = el('input', { attrs: { type: 'checkbox', checked: 'true' } }) as HTMLInputElement;
    const selectAllRow = el('label', { className: 'rz-filter-check-item' }, [
      selectAllCb,
      el('span', { text: '(Select all)' }),
    ]);

    checklist.appendChild(selectAllRow);

    const itemCheckboxes: { val: string; cb: HTMLInputElement; rowEl: HTMLElement }[] = [];

    sortedValues.forEach((val) => {
      const cb = el('input', { attrs: { type: 'checkbox', checked: 'true' } }) as HTMLInputElement;
      const rowEl = el('label', { className: 'rz-filter-check-item' }, [cb, el('span', { text: val })]);
      itemCheckboxes.push({ val, cb, rowEl });
      checklist.appendChild(rowEl);
    });

    if (hasBlanks) {
      const cb = el('input', { attrs: { type: 'checkbox', checked: 'true' } }) as HTMLInputElement;
      const rowEl = el('label', { className: 'rz-filter-check-item' }, [
        cb,
        el('span', { text: '(Blanks)', attrs: { style: 'color: #94a3b8; font-style: italic;' } }),
      ]);
      itemCheckboxes.push({ val: '', cb, rowEl });
      checklist.appendChild(rowEl);
    }

    selectAllCb.addEventListener('change', () => {
      const checked = selectAllCb.checked;
      itemCheckboxes.forEach((item) => {
        if (item.rowEl.style.display !== 'none') {
          item.cb.checked = checked;
        }
      });
    });

    searchInput.addEventListener('input', () => {
      const q = searchInput.value.toLowerCase();
      itemCheckboxes.forEach((item) => {
        const match = item.val.toLowerCase().includes(q) || (item.val === '' && '(blanks)'.includes(q));
        item.rowEl.style.display = match ? 'flex' : 'none';
      });
    });

    // Section 3: Footer Buttons
    const resetBtn = el('button', { className: 'rz-btn-reset', text: 'Reset' });
    const applyBtn = el('button', { className: 'rz-btn-apply', text: 'Apply filter' });

    resetBtn.addEventListener('click', () => {
      closeColFilterPopup();
      void dispatch({ k: 'clearFilters', sheet: currentSheet }).then(() => setNotice('Filter reset'));
    });

    applyBtn.addEventListener('click', () => {
      closeColFilterPopup();
      const checkedVals = itemCheckboxes.filter((i) => i.cb.checked).map((i) => i.val);
      if (checkedVals.length === itemCheckboxes.length) {
        void dispatch({ k: 'clearFilters', sheet: currentSheet }).then(() => setNotice('Filter cleared'));
      } else if (checkedVals.length === 0) {
        void dispatch({ k: 'setFilter', sheet: currentSheet, col, cond: { k: 'isBlank' } }).then(() => setNotice('Filtered empty'));
      } else {
        void dispatch({ k: 'setFilter', sheet: currentSheet, col, cond: { k: 'inList', values: checkedVals } }).then(() => setNotice('Filter applied'));
      }
    });

    const footer = el('div', { className: 'rz-filter-footer' }, [resetBtn, applyBtn]);

    popup.append(
      sortTitle,
      sortAsc,
      sortDesc,
      sortColor,
      divider1,
      filterTitleRow,
      searchBox,
      checklist,
      footer,
    );

    document.body.appendChild(popup);
    // Discard our popup if a newer openInlineColumnFilter call started while
    // we were building. Without this, rapid clicks on different column
    // headers could leave the previous popup orphaned in the DOM.
    if (myGen !== colFilterBuildGen) {
      popup.remove();
      return;
    }
    activeColFilterPopup = popup;

    // Stop mousedown propagation inside the popup so the global outside-click
    // handler does not see clicks on the popup as "outside" and dismiss it.
    // This is what stops the flicker when the user moves their mouse across the
    // popup while it is open.
    popup.addEventListener('mousedown', (ev) => ev.stopPropagation());

    // Re-arm the outside-click suppression after the popup has been mounted so
    // any subsequent click outside still works as a dismiss.
    requestAnimationFrame(() => {
      suppressColFilterOutsideClick = false;
      if (activeColFilterPopup === popup) searchInput.focus();
    });
  }

  const tabs: SheetTabs = createSheetTabs({
    onActivate: (sheet: SheetId) => void activateSheet(sheet),
    onAdd: () => void addSheet(),
    onRename: (sheet: SheetId, name: string) => void dispatch({ k: 'renameSheet', sheet, name }),
    onDelete: (sheet: SheetId) => void deleteSheet(sheet),
  }, statusBar.element);

  /**
   * The pivot builder, loaded on demand (Requirements 8.1, 14.3).
   *
   * The dynamic `import()` here is what makes the bundler emit the panel — and,
   * through the kernel host, the Pivot_Engine — as chunks outside the initial
   * payload. The promise is cached, so the second click costs nothing.
   */
  let pivotPanel: Promise<PivotPanel> | null = null;

  function loadPivotPanel(): Promise<PivotPanel> {
    pivotPanel ??= import('./pivotPanel.js').then((module) => {
      const panel = module.createPivotPanel({
        source: () => grid.selection().range,
        // Two columns clear of the used range, so the default placement does not
        // land on the data the user just selected (Requirement 8.8).
        target: () => {
          const used = grid.sheetView()?.usedRange;
          return {
            sheet: grid.sheet(),
            row: 0,
            col: used === undefined ? 4 : Math.max(used.c1, grid.selection().range.c1) + 2,
          };
        },
        dispatch,
        onClose: () => grid.focus(),
        onMessage: setNotice,
      });
      shellRoot.appendChild(panel.element);
      return panel;
    });
    return pivotPanel;
  }

  async function openPivotPanel(): Promise<PivotPanel> {
    const panel = await loadPivotPanel();
    panel.open();
    return panel;
  }

  /**
   * Loads the pivot feature chunks without building anything (Requirement 14.3).
   *
   * The panel comes from this thread's dynamic import; the Pivot_Engine comes from
   * the kernel worker's, which is triggered by sending it a pivot command — a refresh
   * of a pivot that does not exist. The command is answered with a rejection, having
   * changed nothing, and the engine is resident afterwards.
   *
   * This exists for the local-only integration test, which aborts every request in
   * order to prove that a pivot *computation* needs no network. Fetching the feature's
   * code is a different event from running it, and this separates the two.
   */
  async function warmPivot(): Promise<void> {
    await loadPivotPanel();
    await client.dispatch({ k: 'refreshPivot', id: 'warm-up' }).catch(() => undefined);
  }

  /**
   * The chart panel, loaded on demand (Requirements 9.1, 14.3).
   *
   * Same seam as the pivot builder above: this thread's dynamic `import()` puts the
   * panel and the canvas painter in their own chunk, and the kernel worker's puts the
   * extraction pass in its own. The promise is cached, so the second click costs
   * nothing.
   */
  let chartPanel: Promise<ChartPanel> | null = null;

  function loadChartPanel(): Promise<ChartPanel> {
    chartPanel ??= import('./chartPanel.js').then((module) => {
      const panel = module.createChartPanel({
        source: () => grid.selection().range,
        dispatch,
        fetch: (id) => client.chart(id),
        onClose: () => grid.focus(),
        onMessage: setNotice,
      });
      shellRoot.appendChild(panel.element);
      return panel;
    });
    return chartPanel;
  }

  async function openChartPanel(): Promise<ChartPanel> {
    const panel = await loadChartPanel();
    panel.open();
    return panel;
  }

  // ---------------------------------------------------------------- Modal helper
  let activeModalClose: (() => void) | null = null;

  function closeActiveModal(): void {
    if (activeModalClose !== null) {
      const fn = activeModalClose;
      activeModalClose = null;
      fn();
    }
  }

  /** A generic modal dialog with a backdrop, a card, and a Close button. */
  function openModal(opts: {
    title: string;
    body: HTMLElement;
    width?: number;
    actions?: { label: string; onClick: () => void; primary?: boolean }[];
  }): () => void {
    closeActiveModal();
    const backdrop = el('div', { className: 'ws-modal-backdrop' });
    const card = el('div', { className: 'ws-filter-card' });
    card.style.width = `${opts.width ?? 460}px`;
    card.style.maxHeight = '80vh';
    card.style.overflow = 'auto';
    card.appendChild(el('h2', { className: 'ws-filter-title', text: opts.title }));
    card.appendChild(opts.body);
    if (opts.actions && opts.actions.length > 0) {
      const row = el('div', { className: 'ws-filter-actions', attrs: { style: 'display:flex;justify-content:flex-end;gap:8px;margin-top:16px;' } });
      opts.actions.forEach((a) => {
        const btn = el('button', {
          text: a.label,
          attrs: { class: a.primary ? 'rz-btn-apply' : 'rz-btn-reset' },
        });
        btn.addEventListener('click', () => a.onClick());
        row.appendChild(btn);
      });
      card.appendChild(row);
    }
    backdrop.appendChild(card);
    backdrop.addEventListener('click', (ev) => {
      if (ev.target === backdrop) dispose();
    });
    document.body.appendChild(backdrop);
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        dispose();
      }
    };
    document.addEventListener('keydown', onKey);
    let disposed = false;
    const dispose = (): void => {
      if (disposed) return;
      disposed = true;
      backdrop.remove();
      document.removeEventListener('keydown', onKey);
      if (activeModalClose === dispose) activeModalClose = null;
    };
    activeModalClose = dispose;
    return dispose;
  }

  // ------------------------------------------------ Standby / Reconnecting Card
  function openStandbyWorkbookModal(messageText: string = 'Preparing workbook...'): void {
    const backdrop = el('div', { className: 'ws-standby-backdrop' });
    const card = el('div', { className: 'ws-standby-card' });

    const topNav = el('div', { className: 'ws-standby-topnav' });
    const backBtn = el('button', { className: 'ws-standby-back', text: '‹  Back home' });
    backBtn.addEventListener('click', () => {
      grid.selectRange({ sheet: grid.selection().range.sheet, r0: 0, c0: 0, r1: 0, c1: 0 });
      dispose();
      setNotice('Returned to sheet origin (A1).');
    });
    topNav.appendChild(backBtn);

    const progressTrack = el('div', { className: 'ws-standby-progress-track' });
    const progressFill = el('div', { className: 'ws-standby-progress-fill' });
    progressTrack.appendChild(progressFill);

    const body = el('div', { className: 'ws-standby-body' });
    const spinner = el('div', { className: 'ws-standby-spinner' });
    const msg = el('h3', { className: 'ws-standby-title', text: messageText });
    const proBadge = el('p', { className: 'ws-standby-pro' });
    const proLink = el('a', { className: 'ws-standby-pro-link', text: 'Upgrade to Pro', attrs: { href: '#', onclick: 'return false;' } });
    proBadge.appendChild(proLink);
    proBadge.appendChild(document.createTextNode(' for prioritized workbook startup'));

    body.append(spinner, msg, proBadge);
    card.append(topNav, progressTrack, body);
    backdrop.appendChild(card);
    document.body.appendChild(backdrop);

    let pct = 0;
    const interval = setInterval(() => {
      pct += Math.floor(Math.random() * 25) + 15;
      if (pct > 100) pct = 100;
      progressFill.style.width = `${pct}%`;
      if (pct >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          dispose();
          setNotice('Workbook ready.');
        }, 400);
      }
    }, 180);

    function dispose(): void {
      clearInterval(interval);
      document.removeEventListener('keydown', onKey);
      backdrop.remove();
    }

    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        dispose();
      }
    };
    document.addEventListener('keydown', onKey);
  }

  // ----------------------------------------------------------- AI Agent panel
  /**
   * A formula-assistant panel that takes a natural-language instruction and
   * composes a formula on the active cell. No LLM is called in local mode —
   * the assistant is rule-based over a small intent vocabulary, so it works
   * offline and never sends cell contents anywhere.
   */
  function openAiPanel(): void {
    const body = el('div');
    const help = el('p', {
      text: 'Describe what you want to compute (e.g. "sum of column B", "average if greater than 10"). Local assistant only — no network calls.',
      attrs: { style: 'margin:0 0 10px 0;color:#475569;font-size:12px;' },
    });
    const input = el('textarea', {
      attrs: { rows: '3', placeholder: 'e.g. sum B2:B10', style: 'width:100%;box-sizing:border-box;padding:8px;border:1px solid #cbd5e1;border-radius:6px;font:inherit;resize:vertical;' },
    }) as HTMLTextAreaElement;
    const status = el('div', { attrs: { style: 'min-height:18px;color:#1e40af;font-size:12px;margin-top:6px;' } });
    const suggest = (text: string): string => {
      const t = text.toLowerCase();
      const sum = /\b(sum|total)\b/.test(t);
      const avg = /\b(average|avg|mean)\b/.test(t);
      const count = /\b(count|how many|number of)\b/.test(t);
      const max = /\b(max|maximum|largest)\b/.test(t);
      const min = /\b(min|minimum|smallest)\b/.test(t);
      const range = text.match(/[a-z]+\d*:\s*[a-z]+\d*/i)?.[0] ?? 'A1:A10';
      if (sum) return `=SUM(${range.toUpperCase()})`;
      if (avg) return `=AVERAGE(${range.toUpperCase()})`;
      if (count) return `=COUNT(${range.toUpperCase()})`;
      if (max) return `=MAX(${range.toUpperCase()})`;
      if (min) return `=MIN(${range.toUpperCase()})`;
      return '';
    };

    const preview = el('div', { attrs: { style: 'font-family:ui-monospace,monospace;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:8px;margin-top:8px;min-height:32px;color:#0f172a;' } });

    input.addEventListener('input', () => {
      const f = suggest(input.value);
      preview.textContent = f || '(no suggestion yet — try "sum A1:A10")';
      status.textContent = f ? 'Suggested formula ready.' : '';
    });

    body.append(help, input, status, preview);

    let close: () => void = () => {};
    close = openModal({
      title: '🤖 AI Assistant',
      width: 480,
      body,
      actions: [
        { label: 'Cancel', onClick: () => close() },
        {
          label: 'Insert formula',
          primary: true,
          onClick: async () => {
            const f = suggest(input.value);
            if (!f) {
              status.textContent = 'No suggestion yet.';
              return;
            }
            const at = grid.selection().active;
            await dispatch({ k: 'setCell', addr: at, input: f });
            setNotice(`Inserted ${f} into ${addrToA1(at)}`);
            close();
          },
        },
      ],
    });
  }

  // ----------------------------------------------------------- Data tools panel
  function openDataPanel(): void {
    const body = el('div');
    const list = el('div', { attrs: { style: 'display:flex;flex-direction:column;gap:8px;' } });

    const action = (
      label: string,
      desc: string,
      onClick: () => void,
    ): HTMLElement => {
      const btn = el('button', {
        text: label,
        attrs: { style: 'text-align:left;padding:10px 12px;border:1px solid #e2e8f0;border-radius:6px;background:#ffffff;cursor:pointer;font:inherit;' },
      });
      btn.addEventListener('mouseenter', () => (btn.style.background = '#f1f5f9'));
      btn.addEventListener('mouseleave', () => (btn.style.background = '#ffffff'));
      btn.addEventListener('click', () => onClick());
      const wrap = el('div');
      wrap.appendChild(btn);
      const d = el('p', { text: desc, attrs: { style: 'margin:2px 0 0 0;color:#64748b;font-size:11px;' } });
      wrap.appendChild(d);
      return wrap;
    };

    list.appendChild(action('📥 Import CSV / TSV / JSONL / ZIP', 'Open a file or ZIP archive and stream it into the workbook. ZIPs group same-format files into one sheet.', () => { openImportModal(); }));
    list.appendChild(action('📤 Export as CSV', 'Download the active sheet as RFC-4180 CSV.', () => { void downloadCsv(); }));
    list.appendChild(action('📤 Export as TSV', 'Download the active sheet as tab-separated values.', () => { void downloadTsv(); }));
    list.appendChild(action('📤 Export as JSONL', 'Download the active sheet as one JSON object per row.', () => { void downloadJsonl(); }));
    list.appendChild(action('📦 Export workbook as ZIP', 'Download every sheet as a CSV inside a ZIP. Sheets with matching columns are merged into one CSV.', () => { void downloadZip(); }));
    list.appendChild(action('🔃 Sort range', 'Open the sort dialog.', () => { setNotice('Sort: select a range, then use Data > Sort A-Z / Z-A.'); }));
    list.appendChild(action('🎚️ Toggle filters', 'Show or hide filter dropdowns on Row 1.', () => toggleFilterMode()));
    list.appendChild(action('🧮 Create Pivot Table', 'Open the pivot builder on the active range.', () => { void openPivotPanel(); }));
    list.appendChild(action('📊 Create Chart', 'Open the chart builder on the active range.', () => { void openChartPanel(); }));

    body.appendChild(list);
    openModal({ title: '🗄️ Data Tools', width: 480, body });
  }

  /**
   * Row Zero exact Import Preview & Settings Dialog.
   * Parses the first ~100KB of the file to render a live data preview table on the left,
   * and Destination, Import type, Header row toggle, and Advanced options on the right.
   */
  async function openImportPreviewModal(file: File): Promise<void> {
    setNotice(`Loading preview for ${file.name}...`);
    let sampleText = '';
    try {
      sampleText = await file.slice(0, 150 * 1024).text();
    } catch (e) {
      console.error('Preview error:', e);
      setNotice('Failed to read file preview.');
      return;
    }

    const isTsv = file.name.endsWith('.tsv');
    const isJsonl = file.name.endsWith('.jsonl');
    const rawLines = sampleText.split(/\r?\n/).filter((l) => l.length > 0).slice(0, 40);

    let detectedDelim = isTsv ? '\t' : ',';
    if (!isTsv && !isJsonl && rawLines.length > 0) {
      const line0 = rawLines[0] ?? '';
      if (line0.includes('\t')) detectedDelim = '\t';
      else if (line0.includes(';')) detectedDelim = ';';
      else if (line0.includes('|')) detectedDelim = '|';
    }

    const parseLine = (line: string, delim: string): string[] => {
      if (isJsonl) {
        try {
          const obj = JSON.parse(line) as Record<string, unknown>;
          return Object.values(obj).map((v) => (v === null ? '' : String(v)));
        } catch {
          return [line];
        }
      }
      const fields: string[] = [];
      let cur = '';
      let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') {
          inQ = !inQ;
        } else if (c === delim && !inQ) {
          fields.push(cur);
          cur = '';
        } else {
          cur += c;
        }
      }
      fields.push(cur);
      return fields;
    };

    let sampleRows: string[][] = rawLines.map((l) => parseLine(l, detectedDelim));

    // Preview state
    let hasHeader = true;
    let destination = 'existing';
    const activeAddr = addrToA1(grid.selection().active);
    const targetCell = activeAddr;
    let activeDelim = detectedDelim;

    const body = el('div', { className: 'rz-preview-dialog-container' });
    const leftPane = el('div', { className: 'rz-preview-left-pane' });
    const rightPane = el('div', { className: 'rz-preview-right-pane' });

    const tableWrapper = el('div', { className: 'rz-preview-table-wrapper' });
    const table = el('table', { className: 'rz-preview-table' });
    tableWrapper.appendChild(table);

    const metaRow = el('div', { className: 'rz-preview-meta-row' });
    const updateMetaText = () => {
      const avgLen = Math.max(1, rawLines.join('\n').length / Math.max(1, rawLines.length));
      const estRows = Math.max(rawLines.length, Math.round(file.size / avgLen));
      metaRow.textContent = `${estRows.toLocaleString()} rows`;
    };
    updateMetaText();

    leftPane.append(tableWrapper, metaRow);

    const renderTable = () => {
      table.replaceChildren();
      if (sampleRows.length === 0) return;

      const headerTr = el('tr');
      const firstRow = sampleRows[0] ?? [];
      const colCount = Math.max(...sampleRows.map((r) => r.length), 1);

      if (hasHeader) {
        firstRow.forEach((h, idx) => {
          headerTr.appendChild(el('th', { text: h || getColName(idx) }));
        });
        table.appendChild(headerTr);

        sampleRows.slice(1).forEach((row) => {
          const tr = el('tr');
          for (let i = 0; i < colCount; i++) {
            tr.appendChild(el('td', { text: row[i] ?? '' }));
          }
          table.appendChild(tr);
        });
      } else {
        for (let i = 0; i < colCount; i++) {
          headerTr.appendChild(el('th', { text: getColName(i) }));
        }
        table.appendChild(headerTr);

        sampleRows.forEach((row) => {
          const tr = el('tr');
          for (let i = 0; i < colCount; i++) {
            tr.appendChild(el('td', { text: row[i] ?? '' }));
          }
          table.appendChild(tr);
        });
      }
    };
    renderTable();

    // Right pane setup:
    // 1. Destination
    const destGroup = el('div', { className: 'rz-preview-field-group' }, [
      el('label', { className: 'rz-preview-field-label', text: 'Destination' }),
    ]);
    const destSelect = el('select', { className: 'rz-preview-select' }, [
      el('option', { text: 'Existing sheet', attrs: { value: 'existing' } }),
      el('option', { text: 'New sheet', attrs: { value: 'new' } }),
    ]) as HTMLSelectElement;

    const cellInput = el('input', {
      className: 'rz-preview-input',
      attrs: { type: 'text', value: targetCell },
    }) as HTMLInputElement;

    destSelect.addEventListener('change', () => {
      destination = destSelect.value;
      cellInput.style.display = destination === 'new' ? 'none' : 'block';
    });

    destGroup.append(destSelect, cellInput);

    // 2. Import type
    const typeGroup = el('div', { className: 'rz-preview-field-group' }, [
      el('label', { className: 'rz-preview-field-label', text: 'Import type' }),
      el('select', { className: 'rz-preview-select' }, [
        el('option', { text: 'Import into cells' }),
      ]),
    ]);

    // 3. Header row toggle
    const toggleRow = el('div', { className: 'rz-toggle-row' }, [
      el('span', { className: 'rz-toggle-label', text: 'Header row' }),
    ]);
    const toggleSwitch = el('label', { className: 'rz-toggle-switch' });
    const toggleCheckbox = el('input', { attrs: { type: 'checkbox', checked: 'checked' } }) as HTMLInputElement;
    const toggleSlider = el('span', { className: 'rz-toggle-slider' });
    toggleSwitch.append(toggleCheckbox, toggleSlider);
    toggleRow.appendChild(toggleSwitch);

    toggleCheckbox.addEventListener('change', () => {
      hasHeader = toggleCheckbox.checked;
      renderTable();
    });

    // 4. Advanced options collapsible
    const advDetails = el('details', { attrs: { style: 'margin-top: 8px; font-size: 12px; color: #475569; cursor: pointer;' } }, [
      el('summary', { text: 'Advanced options', attrs: { style: 'font-weight: 600; padding: 4px 0;' } }),
    ]);
    const advGroup = el('div', { attrs: { style: 'margin-top: 8px; display: flex; flex-direction: column; gap: 6px;' } }, [
      el('label', { className: 'rz-preview-field-label', text: 'Delimiter' }),
    ]);
    const delimSelect = el('select', { className: 'rz-preview-select' }, [
      el('option', { text: 'Comma (,)', attrs: { value: ',' } }),
      el('option', { text: 'Tab (\\t)', attrs: { value: '\t' } }),
      el('option', { text: 'Semicolon (;)', attrs: { value: ';' } }),
      el('option', { text: 'Pipe (|)', attrs: { value: '|' } }),
    ]) as HTMLSelectElement;
    delimSelect.value = activeDelim;
    delimSelect.addEventListener('change', () => {
      activeDelim = delimSelect.value;
      sampleRows = rawLines.map((l) => parseLine(l, activeDelim));
      renderTable();
    });
    advGroup.appendChild(delimSelect);

    // Entertaining dancing dog & cat banner inside white modal space
    const dancingBanner = el('div', {
      className: 'rz-dancing-banner',
    });

    const danceImg = el('img', {
      className: 'rz-dance-img',
      attrs: {
        src: '/dancing_dog_cat.png',
        alt: 'Dancing Dog and Cat',
      },
    });

    const danceCaption = el('div', {
      attrs: { style: 'font-size: 12px; font-weight: 600; color: #0d9488; font-family: sans-serif;' },
      text: '🐶🐱 Dancing while streaming your data live!',
    });

    dancingBanner.append(danceImg, danceCaption);
    rightPane.append(destGroup, typeGroup, toggleRow, advDetails, dancingBanner);

    // Progress bar inside the modal (Row Zero exact behavior)
    const progressTrack = el('div', {
      className: 'rz-modal-progress-track',
      attrs: { style: 'display: none; height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden; margin-top: 14px; width: 100%;' },
    });
    const progressFill = el('div', {
      className: 'rz-modal-progress-fill',
      attrs: { style: 'height: 100%; width: 0%; background: linear-gradient(90deg, #0d9488, #0284c7); transition: width 0.15s ease;' },
    });
    progressTrack.appendChild(progressFill);

    const progressText = el('div', {
      className: 'rz-modal-progress-text',
      attrs: { style: 'display: none; margin-top: 8px; font-size: 13px; font-weight: 600; color: #0d9488; text-align: center;' },
      text: '⚡ Streaming data into cells live...',
    });

    body.append(leftPane, rightPane, progressTrack, progressText);

    let closeModal: () => void = () => {};
    closeModal = openModal({
      title: `Import ${file.name}`,
      width: 900,
      body,
      actions: [
        {
          label: 'Back',
          onClick: () => {
            closeModal();
            openImportModal();
          },
        },
        {
          label: 'Import',
          primary: true,
          onClick: async () => {
            // Find modal action buttons and disable them during stream
            const actionBtns = body.parentElement?.querySelectorAll<HTMLButtonElement>('button') ?? [];
            actionBtns.forEach((b) => {
              b.disabled = true;
              if (b.classList.contains('rz-btn-apply')) {
                b.textContent = 'Importing...';
              }
            });

            progressTrack.style.display = 'block';
            progressText.style.display = 'block';
            progressFill.style.width = '5%';

            let sheetId = grid.selection().range.sheet;
            if (destination === 'new') {
              const res = await addSheet();
              if (res.ok) {
                sheetId = grid.selection().range.sheet;
              }
            }

            importAbort?.abort();
            const ac = new AbortController();
            importAbort = ac;

            const avgLen = Math.max(1, rawLines.join('\n').length / Math.max(1, rawLines.length));
            const estTotalRows = Math.max(100, Math.round(file.size / avgLen));

            try {
              grid.setPaintPaused(true);
              if (file.name.toLowerCase().endsWith('.zip')) {
                await streamZipImport(
                  client,
                  file,
                  {
                    sheetId,
                    hasHeader,
                    onProgress: ({ rows }) => {
                      const pct = Math.min(95, Math.round((rows / estTotalRows) * 100));
                      progressFill.style.width = `${pct}%`;
                      progressText.textContent = `⚡ Streaming data live... ${rows.toLocaleString()} rows imported (${pct}%)`;
                    },
                  },
                  ac.signal,
                );
              } else if (file.name.toLowerCase().endsWith('.jsonl')) {
                const stream = (file as Blob & { stream: () => ReadableStream<Uint8Array> })
                  .stream()
                  .pipeThrough(new TextDecoderStream('utf-8'));
                const reader = stream.getReader();
                let buffer = '';
                let rowIndex = 0;
                const commands: Command[] = [];
                const BATCH = 4096;
                while (!ac.signal.aborted) {
                  const { value, done } = await reader.read();
                  if (done) break;
                  buffer += value;
                  const linesArr = buffer.split(/\r?\n/);
                  buffer = linesArr.pop() ?? '';
                  for (const line of linesArr) {
                    if (line.length === 0) continue;
                    try {
                      const obj = JSON.parse(line) as Record<string, unknown>;
                      let col = 0;
                      for (const [, v] of Object.entries(obj)) {
                        if (col >= 16384) break;
                        const text = v === null ? '' : String(v);
                        if (text.length > 0) {
                          commands.push({
                            k: 'setCell',
                            addr: { sheet: sheetId, row: rowIndex, col },
                            input: text,
                          });
                        }
                        col += 1;
                      }
                    } catch {
                      // Skip malformed lines
                    }
                    rowIndex += 1;
                    if (commands.length >= BATCH) {
                      await client.dispatchBatch(commands, `JSONL import @${rowIndex}`);
                      commands.length = 0;
                      const pct = Math.min(95, Math.round((rowIndex / estTotalRows) * 100));
                      progressFill.style.width = `${pct}%`;
                      progressText.textContent = `⚡ Streaming data live... ${rowIndex.toLocaleString()} rows imported (${pct}%)`;
                    }
                  }
                }
                if (commands.length > 0) {
                  await client.dispatchBatch(commands, `JSONL import final`);
                }
              } else {
                if (file.size >= 5 * 1024 * 1024 && file.name.toLowerCase().endsWith('.csv')) {
                  const tableName = `import_${Date.now().toString(36)}`;
                  try {
                    const response = await uploadColumnarFile(file, tableName, ac.signal);
                    const payload = await response.json() as { ok?: boolean; table?: string; rowCount?: number; columnCount?: number; error?: string };
                    if (response.ok && payload.ok && payload.table && payload.rowCount && payload.columnCount) {
                      const next = new URL(window.location.href);
                      next.searchParams.set('remoteTable', payload.table);
                      next.searchParams.set('remoteRows', String(payload.rowCount));
                      next.searchParams.set('remoteCols', String(payload.columnCount));
                      window.location.assign(next.toString());
                      return;
                    }
                  } catch {
                    // Static-only deployments do not expose the Node DuckDB API.
                  }
                  setNotice('Server DuckDB unavailable; using local streaming import.');
                }
                await streamImport(
                  client,
                  file,
                  {
                    sheetId,
                    delimiter: activeDelim,
                    hasHeader,
                    onProgress: ({ rows }) => {
                      const pct = Math.min(95, Math.round((rows / estTotalRows) * 100));
                      progressFill.style.width = `${pct}%`;
                      progressText.textContent = `⚡ Streaming data live... ${rows.toLocaleString()} rows imported (${pct}%)`;
                    },
                  },
                  ac.signal,
                );
              }
              progressFill.style.width = '100%';
              await grid.reload();
              closeModal();
              grid.focus();
              setNotice(`Successfully imported ${file.name}`);
            } catch (err) {
              console.error('Import error:', err);
              setNotice(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
              closeModal();
            } finally {
              grid.setPaintPaused(false);
              await grid.reload();
              grid.requestPaint();
              if (importAbort === ac) importAbort = null;
              fileInput.value = '';
            }
          },
        },
      ],
    });
  }

  // ----------------------------------------------------------- Row Zero Style Import Modal
  function openImportModal(): void {
    const body = el('div', { className: 'rz-import-modal-body' });

    const headerDesc = el('div', {
      className: 'rz-import-header-desc',
      text: 'Drag and drop your spreadsheet or dataset file to stream millions of rows directly into your workbook.',
    });

    // Drag and drop zone
    const dropZone = el('div', { className: 'rz-import-dropzone' });

    const icon = el('div', {
      className: 'rz-import-icon-badge',
      text: '⇪',
    });

    const title = el('div', {
      className: 'rz-import-dropzone-title',
      text: 'Select files from your computer...',
    });

    const subtitle = el('div', {
      className: 'rz-import-dropzone-subtitle',
      text: 'or drag and drop CSV, TSV, JSONL, or ZIP archives here',
    });

    dropZone.append(icon, title, subtitle);

    let selectedFile: File | null = null;

    dropZone.addEventListener('click', () => {
      fileInput.click();
    });

    dropZone.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      dropZone.classList.add('is-dragover');
    });

    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('is-dragover');
    });

    dropZone.addEventListener('drop', (ev) => {
      ev.preventDefault();
      dropZone.classList.remove('is-dragover');
      const file = ev.dataTransfer?.files?.[0];
      if (file) {
        selectedFile = file;
        closeModal();
        void openImportPreviewModal(file);
      }
    });

    // Format pills section
    const formatsSection = el('div', {}, [
      el('div', { className: 'rz-import-section-label', text: 'Supported Formats' }),
    ]);

    const formatsRow = el('div', { className: 'rz-import-formats-row' });
    [
      { name: 'CSV', icon: '📄' },
      { name: 'TSV', icon: '📊' },
      { name: 'JSONL', icon: '📦' },
      { name: 'Parquet', icon: '⚡' },
      { name: 'XLSX (beta)', icon: '✨' },
      { name: 'ZIP Archive', icon: '🗜️' },
    ].forEach((fmt) => {
      const badge = el('span', { className: 'rz-import-format-pill' }, [
        el('span', { text: fmt.icon }),
        el('span', { text: fmt.name }),
      ]);
      formatsRow.appendChild(badge);
    });
    formatsSection.appendChild(formatsRow);

    // URL section
    const urlSection = el('div', {}, [
      el('div', { className: 'rz-import-section-label', text: '🔗 Or import from Web URL' }),
    ]);

    const urlInput = el('input', {
      className: 'rz-import-url-input',
      attrs: {
        type: 'text',
        placeholder: 'https://example.com/dataset.csv',
      },
    }) as HTMLInputElement;

    urlSection.appendChild(urlInput);

    body.append(headerDesc, dropZone, formatsSection, urlSection);

    let closeModal: () => void = () => {};
    closeModal = openModal({
      title: '📂 Import Data Files',
      width: 560,
      body,
      actions: [
        { label: 'Cancel', onClick: () => closeModal() },
        {
          label: '🚀 Next',
          primary: true,
          onClick: () => {
            if (selectedFile) {
              closeModal();
              void openImportPreviewModal(selectedFile);
            } else if (urlInput.value.trim().length > 0) {
              closeModal();
              setNotice(`Fetching from URL: ${urlInput.value.trim()}...`);
            } else {
              fileInput.click();
            }
          },
        },
      ],
    });
  }

  // ----------------------------------------------------------- Code snippets panel

  // ----------------------------------------------------------- Code snippets panel
  function openCodePanel(): void {
    const body = el('div');
    const snippets: { name: string; code: string; desc: string }[] = [
      { name: 'SUM range', code: '=SUM(A1:A100)', desc: 'Sum every numeric cell in a column.' },
      { name: 'AVERAGE', code: '=AVERAGE(B1:B100)', desc: 'Arithmetic mean.' },
      { name: 'COUNTIF', code: '=COUNTIF(C1:C100, ">0")', desc: 'Count cells that match a condition.' },
      { name: 'SUMIF', code: '=SUMIF(D1:D100, "yes", E1:E100)', desc: 'Sum where the criterion is met.' },
      { name: 'VLOOKUP', code: '=VLOOKUP(A2, Sheet2!A:B, 2, FALSE)', desc: 'Look up A2 in Sheet2 column A, return column B.' },
      { name: 'XLOOKUP', code: '=XLOOKUP(A2, B2:B100, C2:C100, "missing")', desc: 'Modern lookup with a default value.' },
      { name: 'IF', code: '=IF(A1>10, "big", "small")', desc: 'Conditional value.' },
      { name: 'CONCAT', code: '=CONCAT(A1, " ", B1)', desc: 'Join strings.' },
      { name: 'UNIQUE', code: '=UNIQUE(A1:A100)', desc: 'Distinct values (spills).' },
      { name: 'TODAY', code: '=TODAY()', desc: 'Today\'s date.' },
      { name: 'ROW number', code: '=ROW()', desc: 'Current row index.' },
      { name: 'COLUMN letter', code: '=ADDRESS(ROW(), COLUMN(), 4)', desc: 'Cell\'s A1 reference.' },
    ];
    const list = el('div', { attrs: { style: 'display:grid;grid-template-columns:1fr 1fr;gap:8px;' } });
    snippets.forEach((s) => {
      const card = el('div', {
        attrs: {
          style: 'border:1px solid #e2e8f0;border-radius:6px;padding:10px;background:#ffffff;cursor:pointer;',
        },
      });
      card.appendChild(el('strong', { text: s.name }));
      card.appendChild(el('p', { text: s.desc, attrs: { style: 'margin:4px 0;color:#64748b;font-size:11px;' } }));
      card.appendChild(el('code', { text: s.code, attrs: { style: 'display:block;background:#f8fafc;padding:4px 6px;border-radius:4px;font-size:12px;color:#0f172a;margin-top:6px;' } }));
      card.addEventListener('click', async () => {
        const at = grid.selection().active;
        await dispatch({ k: 'setCell', addr: at, input: s.code });
        setNotice(`Inserted ${s.code} at ${addrToA1(at)}`);
      });
      list.appendChild(card);
    });
    body.appendChild(list);
    openModal({ title: '</> Code Snippets', width: 640, body });
  }

  // ----------------------------------------------------------- Tips dialog
  function openTipsDialog(): void {
    const body = el('div');
    const tips: string[] = [
      'Press Ctrl+= or start a cell with = to enter a formula.',
      'Ctrl+Shift+L toggles filter dropdowns on the header row.',
      'Click a column header letter to select an entire column.',
      'Press F4 inside a formula to cycle anchor ($) on the reference under the cursor.',
      'Ctrl+Shift+V pastes values only (no formulas).',
      'Ctrl+\\ selects the entire used range.',
      'Ctrl+1 opens the Format Cells dialog (coming soon).',
      'Ctrl+/ opens the keyboard shortcuts cheat sheet.',
      'Press Escape to dismiss any open popover.',
    ];
    const ul = el('ul', { attrs: { style: 'margin:0;padding-left:18px;color:#0f172a;line-height:1.6;' } });
    tips.forEach((t) => ul.appendChild(el('li', { text: t })));
    body.appendChild(ul);
    openModal({ title: '💡 Tips & Tricks', width: 480, body });
  }

  // --- Header Construction ---

  const fileInput = el('input', {
    attrs: { type: 'file', accept: '.csv,.tsv,.txt,.jsonl,.zip', style: 'display:none;' },
  }) as HTMLInputElement;

  let importAbort: AbortController | null = null;

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    void openImportPreviewModal(file);
  });

  const docTitleInput = el('input', {
    className: 'ws-doc-title',
    attrs: { value: 'Untitled Workbook', spellcheck: 'false' },
  }) as HTMLInputElement;

  const downloadCsv = async () => {
    await downloadExportFor('csv');
  };

  const downloadTsv = async () => {
    await downloadExportFor('tsv');
  };

  const downloadJsonl = async () => {
    await downloadExportFor('jsonl');
  };

  const downloadZip = async (): Promise<void> => {
    setNotice('Building workbook ZIP...');
    try {
      const result = await exportWorkbookZip(client, {
        onProgress: ({ sheet, rows }) =>
          setNotice(`Packaging ${sheet} (${rows.toLocaleString()} rows)`),
      });
      const baseName = (docTitleInput.value || 'workbook').replace(/[\\/:*?"<>|]/g, '_');
      const filename = `${baseName}.zip`;
      downloadWorkbookZip(result.blob, filename);
      const mergedNote = result.mergedSheets.length > 0
        ? ` Merged ${result.mergedSheets.length} same-format group(s): ${result.mergedSheets.map((m) => m.fileName).join(', ')}.`
        : '';
      setNotice(
        `Downloaded ${filename} (${result.files.length} file(s)${mergedNote}).`,
      );
    } catch (err) {
      setNotice(`ZIP export failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  async function downloadExportFor(format: ExportFormat): Promise<void> {
    const currentSheet = grid.selection().range.sheet;
    setNotice(`Exporting ${format.toUpperCase()}...`);
    try {
      const result = await exportSheet(client, {
        format,
        sheet: currentSheet,
        onProgress: ({ rows }) => setNotice(`Exporting ${format.toUpperCase()}... ${rows.toLocaleString()} rows processed`),
      });
      const baseName = (docTitleInput.value || 'workbook').replace(/[\\/:*?"<>|]/g, '_');
      const ext = format === 'jsonl' ? 'jsonl' : format;
      const filename = `${baseName}.${ext}`;
      downloadExport(result.text, filename, format);
      setNotice(`Downloaded ${filename} (${result.rows.toLocaleString()} rows, ${result.cells.toLocaleString()} cells)`);
    } catch (err) {
      setNotice(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  const shareWorkbook = async (): Promise<void> => {
    try {
      if (navigator.clipboard?.writeText !== undefined) {
        await navigator.clipboard.writeText(window.location.href);
        setNotice('Workbook link copied to clipboard!');
        return;
      }
      throw new Error('clipboard API not available');
    } catch (e: unknown) {
      // Fallback: select-and-copy via a hidden textarea, which works on
      // file:// and on http://localhost where the async Clipboard API is
      // gated behind user-activation + secure context.
      try {
        const ta = document.createElement('textarea');
        ta.value = window.location.href;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        setNotice(ok ? 'Workbook link copied (fallback).' : 'Copy failed; URL is in the address bar.');
      } catch {
        setNotice(`Copy failed: ${e instanceof Error ? e.message : String(e)}. URL: ${window.location.href}`);
      }
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Data Command Center Architecture (Nav Rail, Top Header, Contextual Actions)
  // ══════════════════════════════════════════════════════════════════════════

  const applyStylePatch = (patch: CellStylePatch) => {
    void dispatch({ k: 'applyStyle', range: grid.selection().range, patch });
  };

  const formatDrawer = createFormatDrawer({
    onApplyStyle: applyStylePatch,
    onApplyNumberFormat: (fmt: NumberFormat) => {
      void dispatch({ k: 'applyNumberFormat', range: grid.selection().range, fmt });
    },
    onMerge: () => {
      void dispatch({ k: 'merge', range: grid.selection().range });
    },
  });

  const addDataModal = createAddDataModal({
    onImportFile: (file: File) => {
      void openImportPreviewModal(file);
    },
    onConnectDatabase: () => {
      openDataPanel();
    },
    onConnectApi: () => {
      openDataPanel();
    },
  });

  // Helper for duplicateActiveSheet
  void duplicateActiveSheet;

  // 1. Left Navigation Rail
  let railCollapsed = false;
  const navItems = [
    { id: 'home', icon: '⚡', label: 'Command Hub' },
    { id: 'workbooks', icon: '📁', label: 'Workbooks' },
    { id: 'data', icon: '🗄️', label: 'Data Sources' },
    { id: 'imports', icon: '📥', label: 'Imports' },
    { id: 'queries', icon: '🔍', label: 'SQL / DuckDB' },
    { id: 'charts', icon: '📊', label: 'Visualizations' },
    { id: 'automations', icon: '🤖', label: 'AI & Python' },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
  ];

  let activeNavId = 'home';
  const railNavList = el('div', { className: 'rz-rail-nav-list' });

  function renderNavRailItems(): void {
    railNavList.replaceChildren();
    navItems.forEach((item) => {
      const btn = el('button', {
        className: `rz-rail-item ${item.id === activeNavId ? 'is-active' : ''}`,
        attrs: { title: item.label, 'aria-label': item.label },
      }, [
        el('span', { className: 'rz-rail-item-icon', text: item.icon }),
        el('span', { className: 'rz-rail-item-label', text: item.label }),
      ]);

      btn.addEventListener('click', () => {
        activeNavId = item.id;
        renderNavRailItems();
        if (item.id === 'imports' || item.id === 'data') addDataModal.open();
        else if (item.id === 'charts') void openChartPanel();
        else if (item.id === 'queries') void openPivotPanel();
        else if (item.id === 'automations') openAiPanel();
        else if (item.id === 'settings') cheatSheet.open();
        else if (item.id === 'workbooks') openStandbyWorkbookModal('Switching workbook...');
      });

      railNavList.appendChild(btn);
    });
  }
  renderNavRailItems();

  const navRail = el('aside', { className: 'rz-nav-rail', attrs: { 'aria-label': 'Main Navigation Rail' } }, [
    el('div', { className: 'rz-rail-top' }, [
      el('div', { className: 'rz-rail-brand' }, [
        el('div', { className: 'rz-rail-logo' }, [
          el('div', { className: 'rz-rail-icon', text: 'NS' }),
          el('span', { className: 'rz-rail-title', text: 'NumixSheet' }),
        ]),
        el('button', {
          className: 'rz-rail-collapse-btn',
          text: '◀',
          attrs: { title: 'Collapse sidebar', 'aria-label': 'Toggle sidebar' },
        }),
      ]),
      railNavList,
    ]),
    el('div', { className: 'rz-rail-bottom' }, [
      el('button', {
        className: 'rz-rail-item',
        attrs: { title: 'Keyboard Shortcuts (Ctrl+/)', 'aria-label': 'Shortcuts' },
      }, [
        el('span', { className: 'rz-rail-item-icon', text: '⌨️' }),
        el('span', { className: 'rz-rail-item-label', text: 'Shortcuts' }),
      ]),
    ]),
  ]);

  const collapseBtn = navRail.querySelector('.rz-rail-collapse-btn') as HTMLButtonElement;
  collapseBtn.addEventListener('click', () => {
    railCollapsed = !railCollapsed;
    navRail.classList.toggle('is-collapsed', railCollapsed);
    collapseBtn.textContent = railCollapsed ? '▶' : '◀';
  });

  const shortcutsRailBtn = navRail.querySelector('.rz-rail-bottom .rz-rail-item') as HTMLButtonElement;
  shortcutsRailBtn.addEventListener('click', () => cheatSheet.open());

  // 2. Top Header (Command Center)
  const cmdTrigger = el('div', {
    className: 'rz-header-command-trigger',
    attrs: { role: 'button', 'aria-label': 'Open command palette', title: 'Press Ctrl+K' },
  }, [
    el('div', { className: 'rz-trigger-left' }, [
      el('span', { text: '⚡' }),
      el('span', { text: 'What do you want to do?' }),
    ]),
    el('kbd', { className: 'rz-trigger-kbd', text: 'Ctrl+K' }),
  ]);

  cmdTrigger.addEventListener('click', () => commandPalette.open());

  const topHeader = el('header', { className: 'rz-top-header' }, [
    el('button', {
      className: 'rz-header-workspace-select',
      attrs: { title: 'Switch Active Workspace' },
    }, [
      el('span', { text: '🏢 Production Workspace ▾' }),
    ]),
    cmdTrigger,
    el('div', { className: 'rz-header-actions' }, [
      el('div', { className: 'rz-save-indicator' }, [
        el('span', { className: 'rz-save-dot' }),
        el('span', { text: 'Saved' }),
      ]),
      el('button', {
        className: 'rz-header-action-btn',
        text: '🔔',
        attrs: { title: 'Notifications' },
      }),
      el('button', {
        className: 'rz-header-action-btn',
        text: '💡',
        attrs: { title: 'Tips & Help' },
      }),
      el('button', {
        className: 'rz-share-primary-btn',
        text: '👥 Share',
        attrs: { title: 'Share spreadsheet read-only link' },
      }),
      el('div', {
        className: 'rz-user-profile-badge',
        text: 'NS',
        attrs: { title: 'User Profile' },
      }),
    ]),
  ]);
  const presenceStatus = el('button', { className: 'rz-presence-status', text: '● Only you', attrs: { type: 'button', title: 'Active collaborators' } });
  const commentsStatus = el('button', { className: 'rz-comments-status', text: 'Comments 0', attrs: { type: 'button', title: 'Workbook comments' } });
  const commentsList = el('div', { className: 'rz-comments-list' }, [el('p', { className: 'rz-comments-empty', text: 'No comments yet.' })]);
  const commentInput = el('textarea', { className: 'rz-comment-input', attrs: { rows: '3', placeholder: 'Add a comment about the selected cell…', 'aria-label': 'New comment' } });
  const commentSubmit = el('button', { className: 'rz-comment-submit', text: 'Add comment', attrs: { type: 'button' } });
  const commentsPanel = el('aside', { className: 'rz-comments-panel', attrs: { hidden: 'hidden', 'aria-label': 'Workbook comments' } }, [
    el('div', { className: 'rz-comments-heading' }, [el('strong', { text: 'Comments' }), el('button', { className: 'rz-comments-close', text: '×', attrs: { type: 'button', 'aria-label': 'Close comments' } })]),
    commentsList,
    commentInput,
    commentSubmit,
  ]);
  const headerActions = topHeader.querySelector('.rz-header-actions');
  headerActions?.insertBefore(commentsStatus, headerActions.firstChild);
  headerActions?.insertBefore(presenceStatus, headerActions.firstChild);
  const refreshPresence = async (): Promise<void> => {
    const match = typeof window !== 'undefined' ? window.location.hash.match(/workbook=([^&]+)/) : null;
    if (!match) return;
    const users = await cloudSync.getPresence(decodeURIComponent(match[1] ?? '')).catch(() => []);
    presenceStatus.textContent = users.length > 0 ? `● ${users.length + 1} active` : '● Only you';
    presenceStatus.title = users.length > 0 ? `${users.length} collaborator${users.length === 1 ? '' : 's'} active` : 'No other collaborators active';
  };
  void refreshPresence();
  presenceTimer = setInterval(() => { void refreshPresence(); }, 15000);
  presenceStatus.addEventListener('click', () => void refreshPresence());
  const refreshComments = async (): Promise<void> => {
    const match = typeof window !== 'undefined' ? window.location.hash.match(/workbook=([^&]+)/) : null;
    if (!match) return;
    const comments = await cloudSync.getComments(decodeURIComponent(match[1] ?? '')).catch(() => []);
    commentsStatus.textContent = `Comments ${comments.length}`;
    commentsList.replaceChildren(...(comments.length === 0 ? [el('p', { className: 'rz-comments-empty', text: 'No comments yet.' })] : comments.slice(0, 20).map((comment) => el('article', { className: 'rz-comment-item' }, [el('strong', { text: `${comment.authorName} · ${comment.cellA1}` }), el('p', { text: comment.content })]))));
  };
  void refreshComments();
  commentsStatus.addEventListener('click', () => { commentsPanel.hidden = !commentsPanel.hidden; void refreshComments(); });
  commentsPanel.querySelector('.rz-comments-close')?.addEventListener('click', () => { commentsPanel.hidden = true; });
  commentSubmit.addEventListener('click', () => {
    const match = typeof window !== 'undefined' ? window.location.hash.match(/workbook=([^&]+)/) : null;
    const content = commentInput.value.trim();
    if (!match || !content) return;
    const selection = grid.selection();
    void cloudSync.createComment({ workbookId: decodeURIComponent(match[1] ?? ''), sheetId: selection.range.sheet, row: selection.active.row, col: selection.active.col, content }).then(() => { commentInput.value = ''; void refreshComments(); setNotice('Comment added'); });
  });

  const topNotif = topHeader.querySelectorAll('.rz-header-action-btn')[0] as HTMLButtonElement;
  const topHelp = topHeader.querySelectorAll('.rz-header-action-btn')[1] as HTMLButtonElement;
  const topShare = topHeader.querySelector('.rz-share-primary-btn') as HTMLButtonElement;
  const workspaceSelect = topHeader.querySelector('.rz-header-workspace-select') as HTMLButtonElement;

  topNotif.addEventListener('click', () => setNotice('No new notifications (Local Data Engine Active).'));
  topHelp.addEventListener('click', () => openTipsDialog());
  topShare.addEventListener('click', () => void shareWorkbook());
  workspaceSelect.addEventListener('click', () => modePicker.open());
  modePicker.onSelect((mode) => {
    activeProductMode = mode;
    const label = mode === 'spreadsheet' ? 'Spreadsheet' : mode === 'document' ? 'Document' : 'Presentation';
    const labelNode = workspaceSelect.querySelector('span');
    if (labelNode) labelNode.textContent = `▣ ${label} ⌄`;
    document.body.dataset.productMode = mode;
    try { localStorage.setItem('numix-product-mode', mode); } catch { /* storage is optional */ }
    void cloudSync.savePreferences({ productMode: mode });
    if (mode === 'spreadsheet') editorModeWorkspace.close();
    else editorModeWorkspace.open(mode);
    setNotice(`${label} workspace selected`);
  });

  // 3. Dataset Info Banner
  const datasetBanner = el('div', { className: 'rz-dataset-banner' }, [
    el('div', { className: 'rz-dataset-meta-group' }, [
      docTitleInput,
      el('span', { className: 'rz-dataset-badge', text: '⚡ 1,000,000 Rows Capacity' }),
      el('span', { className: 'rz-dataset-badge', text: '📊 18,278 Columns' }),
    ]),
    el('div', { className: 'rz-dataset-meta-group' }, [
      el('span', { className: 'rz-dataset-badge rz-perf-badge', text: '⚡ Engine: WebAssembly DuckDB & SIMD (<22ms freeze)' }),
      el('span', { className: 'rz-dataset-badge', text: '☁️ Autosaved' }),
    ]),
  ]);

  // 4. Dynamic Contextual Action Bar
  const contextualBar = el('div', { className: 'rz-contextual-bar ws-toolbar' });
  const ctxGroup = el('div', { className: 'rz-contextual-actions-group' });
  contextualBar.appendChild(ctxGroup);

  function createCtxBtn(label: string, icon: string, onClick: () => void, isPrimary = false, isDanger = false): HTMLElement {
    const btn = el('button', {
      className: `rz-ctx-btn ${isPrimary ? 'is-primary' : ''} ${isDanger ? 'is-danger' : ''}`,
      attrs: { title: label },
    }, [
      el('span', { text: icon }),
      el('span', { text: label }),
    ]);
    btn.addEventListener('click', onClick);
    return btn;
  }

  function updateContextualActions(selection: SelectionState): void {
    ctxGroup.replaceChildren();

    const isWholeRow = selection.range.c0 === 0 && selection.range.c1 >= 18277;
    const isWholeCol = selection.range.r0 === 0 && selection.range.r1 >= 999999;
    const cellCount = (selection.range.r1 - selection.range.r0 + 1) * (selection.range.c1 - selection.range.c0 + 1);

    if (isWholeRow) {
      // Rows selected context
      ctxGroup.append(
        createCtxBtn('Delete Rows', '🗑️', () => void dispatch({ k: 'deleteRows', sheet: grid.selection().range.sheet, at: grid.selection().range.r0, count: grid.selection().range.r1 - grid.selection().range.r0 + 1 }), false, true),
        createCtxBtn('Filter Selection', '⚡', () => toggleFilterMode()),
        createCtxBtn('Export Range', '⬇️', () => void downloadCsv()),
        createCtxBtn('Format', '🎨', () => formatDrawer.open()),
      );
    } else if (isWholeCol) {
      // Columns selected context
      ctxGroup.append(
        createCtxBtn('Delete Columns', '🗑️', () => void dispatch({ k: 'deleteCols', sheet: grid.selection().range.sheet, at: grid.selection().range.c0, count: grid.selection().range.c1 - grid.selection().range.c0 + 1 }), false, true),
        createCtxBtn('Sort A-Z', '🔤', () => {
          void dispatch({
            k: 'sort',
            range: selection.range,
            keys: [{ col: selection.range.c0, direction: 'asc' }],
          });
        }),
        createCtxBtn('Format Column', '🎨', () => formatDrawer.open()),
        createCtxBtn('Pivot Analysis', '📊', () => void openPivotPanel()),
      );
    } else if (cellCount > 50000) {
      // Large dataset selected context
      ctxGroup.append(
        createCtxBtn('OLAP Query Range', '🔍', () => void openPivotPanel(), true),
        createCtxBtn('Aggregate Stats', '📐', () => void openDataPanel()),
        createCtxBtn('Create Visualization', '📈', () => void openChartPanel()),
        createCtxBtn('Export Delimited', '⬇️', () => void downloadCsv()),
      );
    } else {
      // Standard cell / range context
      ctxGroup.append(
        createCtxBtn('Add Data', '📥', () => addDataModal.open(), true),
        createCtxBtn('Format', '🎨', () => formatDrawer.open()),
        createCtxBtn('Formula / AI', '🤖', () => openAiPanel()),
        createCtxBtn('Filter', '⚡', () => toggleFilterMode()),
        createCtxBtn('Chart', '📊', () => void openChartPanel()),
        createCtxBtn('Pivot Table', '🗄️', () => void openPivotPanel()),
        createCtxBtn('Find / Replace', '🔍', () => findBar.open('find')),
      );
    }
  }

  // Populate command palette items
  const paletteItems: CommandPaletteItem[] = [
    { id: 'add-data', category: 'Data', icon: '📥', title: 'Add Data / Import File', subtitle: 'CSV, XLSX, Parquet, SQLite', shortcut: 'Ctrl+I', run: () => addDataModal.open() },
    { id: 'new-sheet', category: 'Actions', icon: '📄', title: 'Insert New Sheet', subtitle: 'Add blank sheet tab', run: () => void addSheet() },
    { id: 'format-drawer', category: 'Format', icon: '🎨', title: 'Open Formatting Command Drawer', subtitle: 'Numbers, fonts, alignment, colors', run: () => formatDrawer.open() },
    { id: 'filter-toggle', category: 'Data', icon: '⚡', title: 'Toggle Filter Mode', subtitle: 'Filter and sort active column', shortcut: 'Ctrl+Shift+L', run: () => toggleFilterMode() },
    { id: 'pivot-table', category: 'Data', icon: '📊', title: 'Create Pivot Table', subtitle: 'Columnar aggregation engine', run: () => void openPivotPanel() },
    { id: 'chart', category: 'Data', icon: '📈', title: 'Create Interactive Chart', subtitle: 'Line, bar, scatter, pie charts', run: () => void openChartPanel() },
    { id: 'ai-agent', category: 'Actions', icon: '🤖', title: 'Open AI Formula Assistant', subtitle: 'Generate formulas and SQL queries', run: () => openAiPanel() },
    { id: 'python', category: 'Data', icon: '🐍', title: 'Execute Python & Pyodide Analysis', subtitle: 'In-browser scientific computing', run: () => openCodePanel() },
    { id: 'find', category: 'Actions', icon: '🔍', title: 'Find and Replace', subtitle: 'Search cells across sheet', shortcut: 'Ctrl+F', run: () => findBar.open('find') },
    { id: 'export-csv', category: 'Data', icon: '⬇️', title: 'Download Sheet as CSV', subtitle: 'Comma-separated values', run: () => void downloadCsv() },
    { id: 'export-zip', category: 'Data', icon: '📦', title: 'Download Workbook as ZIP', subtitle: 'Full multi-sheet backup', run: () => void downloadZip() },
    { id: 'shortcuts', category: 'Help', icon: '⌨️', title: 'Keyboard Shortcuts Cheat Sheet', subtitle: 'List all key bindings', shortcut: 'Ctrl+/', run: () => cheatSheet.open() },
    { id: 'share', category: 'Actions', icon: '👥', title: 'Share Spreadsheet', subtitle: 'Copy live workbook URL', run: () => void shareWorkbook() },
  ];

  const commandPalette = createCommandPalette({
    items: paletteItems,
  });

  // Global Ctrl+K / Cmd+K listener
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      commandPalette.open();
    }
  });

  // Mobile Bottom Action Bar
  const mobileActionBar = el('div', { className: 'rz-mobile-action-bar' }, [
    el('button', {
      className: 'rz-mobile-action-btn',
      attrs: { 'aria-label': 'Undo previous action', title: 'Undo' },
    }, [
      el('span', { className: 'rz-action-icon', text: '↩' }),
      el('span', { text: 'Undo' }),
    ]),
    el('button', {
      className: 'rz-mobile-action-btn',
      attrs: { 'aria-label': 'Add Data', title: 'Add Data' },
    }, [
      el('span', { className: 'rz-action-icon', text: '📥' }),
      el('span', { text: 'Data' }),
    ]),
    el('button', {
      className: 'rz-mobile-action-btn',
      attrs: { 'aria-label': 'Filter / Sort', title: 'Filter' },
    }, [
      el('span', { className: 'rz-action-icon', text: '⚡' }),
      el('span', { text: 'Filter' }),
    ]),
    el('button', {
      className: 'rz-mobile-action-btn',
      attrs: { 'aria-label': 'Command Palette', title: 'Commands' },
    }, [
      el('span', { className: 'rz-action-icon', text: '⚡' }),
      el('span', { text: 'Cmd' }),
    ]),
    el('button', {
      className: 'rz-mobile-action-btn',
      attrs: { 'aria-label': 'Edit active cell formula', title: 'Edit' },
    }, [
      el('span', { className: 'rz-action-icon', text: 'fx' }),
      el('span', { text: 'Edit' }),
    ]),
  ]);

  const mUndo = mobileActionBar.children[0] as HTMLButtonElement | undefined;
  const mData = mobileActionBar.children[1] as HTMLButtonElement | undefined;
  const mFilter = mobileActionBar.children[2] as HTMLButtonElement | undefined;
  const mCmd = mobileActionBar.children[3] as HTMLButtonElement | undefined;
  const mEdit = mobileActionBar.children[4] as HTMLButtonElement | undefined;

  if (mUndo) mUndo.addEventListener('click', () => void client.undo());
  if (mData) mData.addEventListener('click', () => addDataModal.open());
  if (mFilter) mFilter.addEventListener('click', () => toggleFilterMode());
  if (mCmd) mCmd.addEventListener('click', () => commandPalette.open());
  if (mEdit) mEdit.addEventListener('click', () => {
    formulaBar.input.focus();
    formulaBar.element.classList.toggle('is-bottom-sheet');
  });

  // Assemble Main Workspace
  const mainWorkspace = el('main', { className: 'rz-main-workspace' }, [
    topHeader,
    datasetBanner,
    contextualBar,
    formulaBar.element,
    findBar.element,
    grid.element,
    tabs.element,
    mobileActionBar,
  ]);

  const shellRoot = el('div', { className: 'ws-shell' }, [
    fileInput,
    navRail,
    mainWorkspace,
    commandPalette.element,
    formatDrawer.element,
    addDataModal.element,
    cheatSheet.element,
    modePicker.element,
    editorModeWorkspace.element,
    commentsPanel,
  ]);

  host.appendChild(shellRoot);
  if (activeProductMode !== 'spreadsheet') editorModeWorkspace.open(activeProductMode);

  /** Refreshes the name box, the formula bar, and the status-bar aggregate. */
  async function refreshSelection(selection: SelectionState): Promise<void> {
    try {
      if (typeof sessionStorage !== 'undefined' && typeof window !== 'undefined') {
        sessionStorage.setItem('numix_live_active_cell', JSON.stringify(selection.active));
      }
      const workbookMatch = typeof window !== 'undefined' ? window.location.hash.match(/workbook=([^&]+)/) : null;
      if (workbookMatch && Date.now() - lastPresenceSentAt >= 1000) {
        lastPresenceSentAt = Date.now();
        void cloudSync.sendPresence({ workbookId: decodeURIComponent(workbookMatch[1] ?? ''), sheetId: selection.range.sheet, cursor: { row: selection.active.row, col: selection.active.col } });
      }
      formulaBar.setSelection(selection.active, selection.range);
      aggregateFor = selection.range;
      const bounds = grid.sheetView()?.usedRange ?? null;
      const [content, aggregate] = await Promise.all([
        client.cellContent(selection.active).catch(() => null),
        aggregator.aggregate({ range: selection.range, bounds }).catch(() => null),
      ]);
      // A reply for a selection the user has already left is dropped: the aggregate of a
      // tall selection can outlive several clicks.
      if (aggregateFor !== null && !sameRange(aggregateFor, selection.range)) return;
      formulaBar.setContent(content);
      statusBar.setAggregate(aggregate);
      updateContextualActions(selection);
    } catch (err: unknown) {
      console.warn('[refreshSelection safe fallback]', err);
    }
  }

  /** Activates a sheet tab (Requirement 1.12). */
  async function activateSheet(sheet: SheetId): Promise<void> {
    if (sheet === grid.sheet()) return;
    const view = await grid.setSheet(sheet);
    tabs.render(view.sheets, view.sheet);
    await grid.settle();
    await refreshSelection(grid.selection());
  }

  /** `Ctrl+PageDown` / `Ctrl+PageUp`: the neighbouring tab, without wrapping. */
  async function stepSheet(delta: 1 | -1): Promise<void> {
    const view = grid.sheetView();
    const order = view?.sheets ?? [];
    const at = order.findIndex((tab) => tab.id === grid.sheet());
    const next = order[at + delta];
    if (next === undefined) {
      setNotice(delta === 1 ? 'This is the last sheet.' : 'This is the first sheet.');
      return;
    }
    await activateSheet(next.id);
    grid.focus();
  }

  /** Adds a sheet, letting the kernel enforce the 10-sheet ceiling (R1.3, R1.11). */
  async function addSheet(): Promise<CommandResult> {
    const before = grid.sheetView()?.sheets ?? [];
    const name = nextSheetName(before);
    const result = await dispatch({ k: 'addSheet', name });
    if (!result.ok) return result;
    const view = await grid.reload();
    tabs.render(view.sheets, view.sheet);
    const added = view.sheets.find((t) => t.name === name);
    if (added !== undefined) await activateSheet(added.id);
    return result;
  }

  /**
   * Cells one "Make a copy" will carry.
   *
   * The copy goes through the clipboard payload, which is a materialized array, so an
   * unbounded used range would build a structure the size of the sheet on the main
   * thread. Past this the copy is refused with a message rather than freezing the tab.
   */
  const MAX_DUPLICATE_CELLS = 1_000_000;

  /**
   * Duplicates the active sheet, values, formulas and formatting included.
   *
   * "Make a copy" used to call {@link addSheet}, so it was indistinguishable from
   * Insert > New Sheet: it added an empty sheet and copied nothing. The duplication
   * reuses the clipboard payload the kernel already builds for `Ctrl+C`, so formulas
   * translate and formats travel exactly as they do for a paste.
   */
  async function duplicateActiveSheet(): Promise<void> {
    const source = grid.sheet();
    const used = grid.sheetView()?.usedRange ?? null;
    if (used === null) {
      setNotice('Nothing to copy: the sheet is empty. Added a blank sheet instead.');
      await addSheet();
      return;
    }
    const cells = (used.r1 - used.r0 + 1) * (used.c1 - used.c0 + 1);
    if (cells > MAX_DUPLICATE_CELLS) {
      setNotice(
        `Too large to copy: ${cells.toLocaleString()} cells, limit ${MAX_DUPLICATE_CELLS.toLocaleString()}. ` +
          'Copy a range and paste it into a new sheet instead.',
      );
      return;
    }
    setNotice('Copying sheet...');
    try {
      const payload = await client.clip({ ...used, sheet: source });
      const added = await addSheet();
      if (!added.ok) {
        setNotice(added.error.message);
        return;
      }
      const target = grid.sheet();
      if (target === source) {
        setNotice('Could not add a sheet to copy into.');
        return;
      }
      // `used` may not start at A1, and a copy should land where the original sat.
      const result = await dispatch({
        k: 'paste',
        at: { sheet: target, row: used.r0, col: used.c0 },
        variant: 'all',
        payload,
        selection: { sheet: target, r0: used.r0, c0: used.c0, r1: used.r0, c1: used.c0 },
      });
      setNotice(
        result.ok
          ? `Copied ${rangeToA1(used)} into a new sheet.`
          : `Sheet added, but the copy failed: ${result.error.message}`,
      );
    } catch (e: unknown) {
      setNotice(e instanceof Error ? e.message : String(e));
    }
  }

  async function deleteSheet(sheet: SheetId): Promise<void> {
    const wasActive = sheet === grid.sheet();
    const result = await dispatch({ k: 'deleteSheet', sheet });
    if (!result.ok) return;
    if (wasActive) {
      // The deleted sheet cannot be reloaded, so move to a surviving tab first. The
      // kernel keeps its own active sheet; the shell follows the tab order.
      const remaining = (grid.sheetView()?.sheets ?? []).filter((t) => t.id !== sheet);
      const next = remaining[0];
      if (next !== undefined) await activateSheet(next.id);
      return;
    }
    const view = await grid.reload();
    tabs.render(view.sheets, view.sheet);
  }

  const unsubscribeChanged = client.onChanged((event) => {
    grid.invalidate(event.changedRegions);
    if (event.notices.length > 0) setNotice(event.notices.join(' · '));
    if (chartPanel !== null) {
      void chartPanel.then((panel) => panel.applyChange(event.changedRegions));
    }
    if (!grid.isPaintPaused()) {
      void grid.reload().then(() => grid.requestPaint());
    }
  });

  /**
   * The long-operation progress indicator (Requirement 14.7).
   *
   * The worker reports `begin` / `update` / `end` only for work it has already found to
   * be slower than a second, so this subscription is what makes the indicator appear
   * for a 5,000,000-row sort and never for a keystroke. Scrolling keeps working while it
   * is up because the work is in the worker and this callback is the only main-thread
   * cost of it: one text update per 100 ms.
   */
  const unsubscribeProgress = client.onProgress((event) => {
    if (event.phase === 'end') {
      statusBar.setProgress(null);
      lastProgress = null;
      return;
    }
    lastProgress = { op: event.op, done: event.done, total: event.total };
    statusBar.setProgress(lastProgress);
  });

  const unsubscribeRecovered = client.onRecovered((report) => {
    grid.cache().clear();
    setNotice(
      `kernel restarted (${report.reason}); replayed ${report.replay?.applied ?? 0} operation(s)`,
    );
    void grid.reload().then(() => grid.requestPaint());
  });

  async function readout(range: Range): Promise<TileReadout> {
    const tile = await client.tile(range);
    const values: CellValue[] = [];
    for (let row = tile.range.r0; row <= tile.range.r1; row++) {
      for (let col = tile.range.c0; col <= tile.range.c1; col++) {
        values.push(tileCell(tile, row, col));
      }
    }
    return { rows: tile.rows, cols: tile.cols, values };
  }

  const handle: SpreadsheetHandle = {
    dispatch: (command) => dispatch(command),
    tile: (range) => readout(range),
    undo: () => client.undo(),
    redo: () => client.redo(),
    diagnostics: () => client.diagnostics(),
    transport: () => client.transport(),
    restarts: () => client.restarts(),
    selection: () => {
      const selection = grid.selection();
      return { active: selection.active, range: selection.range };
    },
    select: (range: Range) => grid.selectRange(range),
    aggregate: () =>
      aggregator.aggregate({
        range: grid.selection().range,
        bounds: grid.sheetView()?.usedRange ?? null,
      }),
    nameBox: () => formulaBar.nameBox.value,
    formulaBar: () => formulaBar.input.value,
    statusText: () => statusBar.element.textContent ?? '',
    sheets: () => {
      const view = grid.sheetView();
      return (view?.sheets ?? []).map((tab) => ({
        id: tab.id,
        name: tab.name,
        active: tab.id === view?.sheet,
      }));
    },
    activateSheet: (sheet: SheetId) => activateSheet(sheet),
    addSheet: () => addSheet(),
    lastFrame: () => lastFrame,
    notice: () => noticeText,
    editing: () => grid.isEditing(),
    editorText: () => grid.editor.session()?.text ?? null,
    shortcuts: (query?: string) =>
      cheatSheet.filter(query ?? '').map((binding) => binding.combo),
    find: (term: string) => findNext(term),
    replaceAll: (term: string, replacement: string) => replaceAll(term, replacement),
    hiddenRows: () => grid.sheetView()?.hiddenRows ?? [],
    progress: () => lastProgress,
    copy: () => copySelection(false),
    cut: () => copySelection(true),
    paste: (variant?: PasteVariant) => pasteInto(variant ?? 'all'),
    warmPivot: () => warmPivot(),
  };

  /**
   * The native paste, for the gestures that are not `Ctrl+V` (Requirement 12.8).
   *
   * `Ctrl+V` never reaches here — the Shortcut_Handler suppresses it per Requirement
   * 3.6 and reads the clipboard itself — so this covers the context menu and X11
   * middle-click, and it is also the only path a browser that refuses
   * `navigator.clipboard.read()` leaves open.
   */
  const onPasteEvent = (event: Event): void => {
    const clipboardEvent = event as Event & {
      clipboardData: { getData: (type: string) => string } | null;
    };
    const read = fromClipboardEvent(clipboardEvent);
    if (read.payload === null) return;
    event.preventDefault();
    void pasteInto('all', read);
  };
  shellRoot.addEventListener('paste', onPasteEvent);

  // Assigned through a cast rather than a `Window` augmentation: the integration suite
  // declares its own view of this handle, and two declarations of one global property
  // would have to agree on a type neither side owns.
  (window as unknown as { __spreadsheet: SpreadsheetHandle }).__spreadsheet = handle;
  (window as unknown as { __pythonEngine: typeof pythonEngine }).__pythonEngine = pythonEngine;

  const ready = (async (): Promise<void> => {
    await client.ready;
    const view = await grid.setSheet(firstSheet);
    tabs.render(view.sheets, view.sheet);
    await grid.settle();
    await refreshSelection(grid.selection());
  })();

  return {
    ready,
    handle,
    grid,
    cheatSheet,
    openPivotPanel,
    openChartPanel,
    sheetView: () => grid.sheetView(),
    announce(text: string): void {
      diagnosticsLine.textContent = text;
    },
    markReady(text = 'Kernel ready.'): void {
      readyLine.textContent = text;
    },
    setNotice,
    dispose(): void {
      unsubscribeChanged();
      unsubscribeProgress();
      unsubscribeRecovered();
      shellRoot.removeEventListener('paste', onPasteEvent);
      document.removeEventListener('keydown', onDocFallbackKeyDown, true);
      document.removeEventListener('mousedown', onDocMouseDown, true);
      if (presenceTimer !== null) { clearInterval(presenceTimer); presenceTimer = null; }
      grid.dispose();
      shellRoot.remove();
    },
  };
}
